import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Bell, Check, Clock, Package, Truck, Store, User, Phone, MapPin,
  CreditCard, Banknote, RefreshCw, Volume2, VolumeX, Wifi, WifiOff,
  ChefHat, CheckCircle2, LogOut, LogIn, Building2, Mail, Lock, Key,
  ShoppingCart, Plus, Minus, X, Search, UtensilsCrossed, Pizza, Salad, Coffee,
  IceCream, Beef, Sandwich, Users, UserPlus, Receipt, Euro, Settings,
  Globe, Bike, Car, PhoneCall, Monitor, AlertCircle, Timer, Utensils, Printer,
  LayoutDashboard, Menu
} from 'lucide-react';

// Mobile POS Components
import { MobileBottomBar, CartDrawer, POSTabNavigation } from '@/components/pos';
import ProductOptionsDialog from '@/components/ProductOptionsDialog';
import ModifierGroupsDialog from '@/components/ModifierGroupsDialog';
import FavoritesPanel from '@/components/FavoritesPanel';
import QuickReorderPanel from '@/components/QuickReorderPanel';
import SplitBillModal from '@/components/SplitBillModal';
import TipModal from '@/components/TipModal';
import ProductCard from '@/components/ProductCard';
import { toast } from 'sonner';

// Station Components
import StationSelector from '@/components/stations/StationSelector';
import KitchenStation from '@/components/stations/KitchenStation';
import DriverStation from '@/components/stations/DriverStation';
import PhoneStation from '@/components/stations/PhoneStation';
import PrinterSettings from '@/components/PrinterSettings';
import MainDashboard from '@/components/dashboard/MainDashboard';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Notification Sound
const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2JkJOPi4J3aF1YXWh0gYuTl5GPh3xxZl1aX2h1g46Wm5iSiH51aWFdYWp3hZCZnpqUi4F4bmVgYGdyfYqUnp6ak4uCeXBnY2Vpdn+MlZ6fnJWNhHx0bGhlanuGj5ifn5yWj4aBenNsaWt3goyWn5+cl5GJgnt1b2xud4OOl5+fnpiSioN9d3JvcnmFj5ifn52Zk4yFgHp2c3V8h5GZn5+dmJONh4J9eXZ4fomSmZ6enJmUjoiDf3x5e4CLlJqenp2alJCKhYF+fH2DjZabnp6dmZWRi4aEgX9/hpCYnJ6enJqWkYyIhYOBhIqTmZyen52bmJOPi4iGhYaLlJqcnp6dnJiUkI2KiIaIjZabnZ6enZyZlZGOi4mIi5CYnJ6enp2cmJWSkI2LioqPlpudnp6enZqXlJGPjYuMkZebnZ6enp2blpSTkI6NjZKYnJ2enp6dm5iVk5GQjo+Um5ydnp6enZuYlpSSkJCRl5ycnZ6enp2bmJaUk5GRk5mbnZ2enp6dm5mXlZOSkpSam5ydnp6enZuZl5aUk5OVm5ydnZ6enp2bmZeWlJSUl5ucnZ2enp6dnJqYl5aVlZebnJ2dnp6enZ2bmpiXlpaYm5ydnZ6enp6dnJqZmJeXmZucnZ2enp6enZybmpmYmJqbnJ2dnp6enp2cm5qZmZmbnJydnZ6enp6dnJuampucnJydnZ6enp6enZyb';

// Category Icons
const CATEGORY_ICONS = {
  'Burger': Beef, 'Pizza': Pizza, 'Wrap': Sandwich, 'Salat': Salad,
  'Pasta': UtensilsCrossed, 'Suppe': Coffee, 'Fingerfood': UtensilsCrossed,
  'Pizzabrötchen': Pizza, 'Beilage': UtensilsCrossed, 'Getränk': Coffee,
  'Dessert': IceCream, 'Kids': UtensilsCrossed, 'Sauce': UtensilsCrossed
};

// Source configurations - Alle Lieferportale mit passenden Farben
const SOURCE_CONFIG = {
  // Lieferportale
  lieferando: { label: 'Lieferando', color: 'bg-[#FF8000]', textColor: 'text-white', icon: Bike, shortLabel: 'LF' },
  wolt: { label: 'Wolt', color: 'bg-[#009DE0]', textColor: 'text-white', icon: Bike, shortLabel: 'WO' },
  ubereats: { label: 'Uber Eats', color: 'bg-[#06C167]', textColor: 'text-white', icon: Car, shortLabel: 'UE' },
  justeat: { label: 'Just Eat', color: 'bg-[#FF5A00]', textColor: 'text-white', icon: Bike, shortLabel: 'JE' },
  deliveroo: { label: 'Deliveroo', color: 'bg-[#00CCBC]', textColor: 'text-white', icon: Bike, shortLabel: 'DL' },
  foodora: { label: 'Foodora', color: 'bg-[#D70F64]', textColor: 'text-white', icon: Bike, shortLabel: 'FO' },
  mjam: { label: 'Mjam', color: 'bg-[#E63C2F]', textColor: 'text-white', icon: Bike, shortLabel: 'MJ' },
  
  // Eigene Kanäle
  website: { label: 'Website', color: 'bg-gradient-to-r from-purple-500 to-pink-500', textColor: 'text-white', icon: Globe, shortLabel: 'WEB' },
  webshop: { label: 'Webshop', color: 'bg-gradient-to-r from-violet-500 to-purple-500', textColor: 'text-white', icon: ShoppingCart, shortLabel: 'SHOP' },
  app: { label: 'App', color: 'bg-gradient-to-r from-blue-500 to-cyan-500', textColor: 'text-white', icon: Monitor, shortLabel: 'APP' },
  
  // Direkt
  phone: { label: 'Telefon', color: 'bg-slate-600', textColor: 'text-white', icon: PhoneCall, shortLabel: 'TEL' },
  pos: { label: 'Vor Ort', color: 'bg-amber-500', textColor: 'text-black', icon: Store, shortLabel: 'POS' },
  counter: { label: 'Theke', color: 'bg-amber-500', textColor: 'text-black', icon: Store, shortLabel: 'TH' },
  
  // Fallback
  api: { label: 'Online', color: 'bg-indigo-500', textColor: 'text-white', icon: Monitor, shortLabel: 'API' },
  online: { label: 'Online', color: 'bg-indigo-500', textColor: 'text-white', icon: Globe, shortLabel: 'ON' },
  unknown: { label: 'Sonstige', color: 'bg-gray-500', textColor: 'text-white', icon: Package, shortLabel: '?' }
};

// Status colors - Modernized with Teal accents
const STATUS_COLORS = {
  new: { bg: 'bg-amber-500', text: 'text-amber-500', label: 'Neu', pulse: true },
  processing: { bg: 'bg-teal-500', text: 'text-teal-500', label: 'In Arbeit', pulse: false },
  ready: { bg: 'bg-emerald-500', text: 'text-emerald-500', label: 'Bereit', pulse: true },
  on_the_way: { bg: 'bg-blue-500', text: 'text-blue-500', label: 'Unterwegs', pulse: false },
  completed: { bg: 'bg-slate-500', text: 'text-slate-500', label: 'Fertig', pulse: false }
};

export default function CashXKasse() {
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [account, setAccount] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Station state - null means show selector, 'hauptkasse' is the main POS
  const [selectedStation, setSelectedStation] = useState(null);
  
  // Dashboard vs POS mode - when 'hauptkasse' is selected
  // 'dashboard' = new sidebar dashboard, 'pos' = old full POS interface
  const [dashboardMode, setDashboardMode] = useState('dashboard');
  
  // Live data
  const [orders, setOrders] = useState({ new: [], processing: [], ready: [] });
  const [tables, setTables] = useState([]);
  const [stats, setStats] = useState(null);
  const [isOnline, setIsOnline] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const previousOrderCountRef = useRef(0);
  const audioRef = useRef(null);
  
  // Tenant Settings (Feature Toggles & Rabatte)
  const [tenantSettings, setTenantSettings] = useState(null);
  
  // POS State
  const [products, setProducts] = useState({});
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [orderTarget, setOrderTarget] = useState('counter'); // counter, table, pickup, delivery
  const [selectedTableForOrder, setSelectedTableForOrder] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  
  // Modals
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);
  const [showPortalConfigModal, setShowPortalConfigModal] = useState(false);
  const [selectedPortal, setSelectedPortal] = useState(null);
  const [showOrderDetailModal, setShowOrderDetailModal] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [processing, setProcessing] = useState(false);
  const [guestCount, setGuestCount] = useState(2);
  const [tableSession, setTableSession] = useState(null);
  
  // FEATURES G & H: Split-Bill & Trinkgeld
  const [showSplitBillModal, setShowSplitBillModal] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);
  const [tipAmount, setTipAmount] = useState('');
  const [splitGuests, setSplitGuests] = useState(2);
  const [lastCompletedOrderId, setLastCompletedOrderId] = useState(null);
  
  // OPTIONAL FEATURE: Product Images
  const [productImageSettings, setProductImageSettings] = useState({
    enabled: false,
    show_on_kasse: true,
    image_size: 'medium',
    show_category: 'all'
  });
  
  // NEW: Delivery/Pickup Modal State
  const [showDeliveryPickupModal, setShowDeliveryPickupModal] = useState(false);
  const [deliveryPickupType, setDeliveryPickupType] = useState(null); // 'pickup' or 'delivery'
  const [deliveryCustomer, setDeliveryCustomer] = useState({
    name: '',
    phone: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    floor: '',
    notes: ''
  });
  const [scheduledDate, setScheduledDate] = useState(new Date().toISOString().split('T')[0]);
  const [scheduledTime, setScheduledTime] = useState('');
  const [isScheduledOrder, setIsScheduledOrder] = useState(false);
  
  // Portal Integrations State
  const [portalConfigs, setPortalConfigs] = useState({
    lieferando: { enabled: true, apiKey: '', storeId: '' },
    wolt: { enabled: true, apiKey: '', merchantId: '' },
    ubereats: { enabled: false, clientId: '', clientSecret: '', storeId: '' },
    justeat: { enabled: false, apiKey: '', restaurantId: '' },
    deliveroo: { enabled: false, apiKey: '', locationId: '' },
    foodora: { enabled: false, apiKey: '', vendorId: '' },
    mjam: { enabled: false, apiKey: '', restaurantId: '' },
    website: { enabled: true, webhookSecret: '' }
  });
  const [webhookTestResult, setWebhookTestResult] = useState(null);
  const [testingWebhook, setTestingWebhook] = useState(false);
  
  // Printer Settings Modal
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  
  // ProductOptions Dialog State (OHNE, Varianten, Extras)
  const [showProductOptions, setShowProductOptions] = useState(false);
  const [selectedProductForOptions, setSelectedProductForOptions] = useState(null);
  
  // Mobile/Tablet State
  const [mobileActiveTab, setMobileActiveTab] = useState('pos'); // 'pos', 'tables', 'pickup', 'delivery'
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Check for mobile/tablet viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Event listener for Dashboard to open Delivery/Pickup modal
  useEffect(() => {
    const handleOpenDeliveryPickup = (event) => {
      // Reset state
      setDeliveryCustomer({
        name: '', phone: '', street: '', houseNumber: '',
        postalCode: '', city: '', floor: '', notes: ''
      });
      setScheduledDate(new Date().toISOString().split('T')[0]);
      setScheduledTime('');
      setIsScheduledOrder(false);
      
      // If detail is 'delivery', skip to delivery form
      if (event.detail === 'delivery') {
        setDeliveryPickupType('delivery');
        setOrderTarget('delivery');
      } else {
        setDeliveryPickupType(null); // Show selection
      }
      
      setShowDeliveryPickupModal(true);
    };
    
    window.addEventListener('openDeliveryPickup', handleOpenDeliveryPickup);
    return () => window.removeEventListener('openDeliveryPickup', handleOpenDeliveryPickup);
  }, []);

  // FEATURE E: Visual Order Type Borders
  const getOrderTypeBorderColor = () => {
    switch(orderTarget) {
      case 'delivery': return 'border-l-4 border-l-blue-500';
      case 'pickup': return 'border-l-4 border-l-emerald-500';
      case 'table': return 'border-l-4 border-l-amber-500';
      default: return 'border-l-4 border-l-slate-700';
    }
  };
  
  // Fetch tenant settings for feature toggles and discounts
  const fetchTenantSettings = useCallback(async (token) => {
    try {
      const response = await fetch(`${API_BASE}/api/tenant-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTenantSettings(data);
        console.log('Tenant settings loaded:', data);
      }
    } catch (error) {
      console.error('Error loading tenant settings:', error);
    }
  }, []);
  
  // Initialize
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND);
    audioRef.current.volume = 0.5;
    const savedToken = localStorage.getItem('access_token');
    const savedAccount = localStorage.getItem('cashx_account');
    if (savedToken && savedAccount) {
      setSessionToken(savedToken);
      setAccount(JSON.parse(savedAccount));
      setIsLoggedIn(true);
      // Load tenant settings on page reload
      fetchTenantSettings(savedToken);
    }
    
    // Check URL parameter for force logout
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('logout') === 'true') {
      handleLogout();
      // Remove parameter from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchTenantSettings]);

  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  // Auth functions - VEREINFACHT: Nur noch SaaS Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      const data = await response.json();
      
      if (response.ok && data.access_token) {
        // Login successful
        setSessionToken(data.access_token);
        setAccount({ 
          account_id: data.tenant?.tenant_id || data.user?.user_id, 
          business_name: data.tenant?.company_name || data.user?.name || 'Mein Restaurant',
          tenant_id: data.tenant?.tenant_id
        });
        setIsLoggedIn(true);
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('cashx_account', JSON.stringify({ 
          account_id: data.tenant?.tenant_id || data.user?.user_id, 
          business_name: data.tenant?.company_name || data.user?.name || 'Mein Restaurant',
          tenant_id: data.tenant?.tenant_id
        }));
        toast.success(`Willkommen bei ${data.tenant?.company_name || 'Cash-X'}!`);
        
        // Load tenant settings (feature toggles & discounts)
        fetchTenantSettings(data.access_token);
      } else {
        toast.error(data.detail || 'E-Mail oder Passwort falsch');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Verbindungsfehler');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setSessionToken(null);
    setAccount(null);
    setSelectedStation(null); // Reset station on logout
    setTenantSettings(null); // Clear tenant settings
    localStorage.removeItem('access_token');
    localStorage.removeItem('cashx_account');
    localStorage.removeItem('cashx_station');
    toast.success('Abgemeldet');
  };

  // Station selection handler
  const handleStationSelect = (stationType) => {
    setSelectedStation(stationType);
    localStorage.setItem('cashx_station', stationType);
    toast.success(`Station "${stationType}" ausgewählt`);
  };

  // Back to station selector
  const handleBackToStationSelector = () => {
    setSelectedStation(null);
    localStorage.removeItem('cashx_station');
  };

  // Data fetching
  const fetchOrders = useCallback(async () => {
    if (!sessionToken) return;
    try {
      const response = await fetch(`${API_BASE}/api/cashx/kasse/orders/active`, { headers: { 'Authorization': `Bearer ${sessionToken}` } });
      if (response.ok) {
        const data = await response.json();
        const newCount = data.new_count || 0;
        if (newCount > previousOrderCountRef.current && previousOrderCountRef.current > 0) {
          playNotificationSound();
          toast.success('🔔 Neue Bestellung!', { duration: 5000 });
        }
        previousOrderCountRef.current = newCount;
        
        // Categorize orders
        const allOrders = [...(data.new || []), ...(data.processing || [])];
        const newOrders = allOrders.filter(o => o.status === 'new');
        const processingOrders = allOrders.filter(o => o.status === 'processing');
        const readyOrders = allOrders.filter(o => o.status === 'ready');
        
        setOrders({ new: newOrders, processing: processingOrders, ready: readyOrders });
        setIsOnline(true);
        setLastUpdate(new Date());
      } else if (response.status === 401) {
        handleLogout();
      }
    } catch (error) {
      setIsOnline(false);
    }
  }, [sessionToken, playNotificationSound]);

  const fetchProducts = useCallback(async () => {
    if (!sessionToken) return;
    try {
      const response = await fetch(`${API_BASE}/api/cashx/kasse/products`, { headers: { 'Authorization': `Bearer ${sessionToken}` } });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || {});
        setCategories(data.categories || []);
        // Set first category as default if none selected
        setSelectedCategory(prev => {
          if (!prev && data.categories?.length > 0) {
            return data.categories[0];
          }
          return prev;
        });
      }
      
      // Load image settings
      const imgResponse = await fetch(`${API_BASE}/api/product-images/settings`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (imgResponse.ok) {
        const imgData = await imgResponse.json();
        setProductImageSettings(imgData);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, [sessionToken]);

  const fetchStats = useCallback(async () => {
    if (!sessionToken) return;
    try {
      const response = await fetch(`${API_BASE}/api/cashx/kasse/stats`, { headers: { 'Authorization': `Bearer ${sessionToken}` } });
      if (response.ok) setStats(await response.json());
    } catch (error) {}
  }, [sessionToken]);

  const fetchTables = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/tables/`);
      if (response.ok) {
        const data = await response.json();
        setTables(data.tables || []);
      }
    } catch (error) {}
  }, []);

  const fetchTableSession = async (tableId) => {
    try {
      const response = await fetch(`${API_BASE}/api/tables/${tableId}/session`);
      if (response.ok) return await response.json();
    } catch (error) {}
    return null;
  };

  // Polling - paused when modals are open to prevent focus loss
  useEffect(() => {
    if (isLoggedIn && sessionToken) {
      fetchOrders();
      fetchStats();
      fetchProducts();
      fetchTables();
      
      // Don't poll when printer settings or other modals are open
      if (showPrinterSettings) {
        return; // Skip interval when modal is open
      }
      
      // OPTIMIZED: Reduced polling from 5s to 30s for better performance
      const interval = setInterval(() => { fetchOrders(); fetchStats(); }, 30000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, sessionToken, fetchOrders, fetchStats, fetchProducts, fetchTables, showPrinterSettings]);

  // Order functions
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/api/cashx/kasse/orders/${orderId}/status?status=${newStatus}`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${sessionToken}` } });
      if (response.ok) {
        toast.success(newStatus === 'processing' ? '👨‍🍳 In Bearbeitung' : newStatus === 'ready' ? '✅ Bereit' : '🎉 Abgeschlossen');
        fetchOrders();
        fetchStats();
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  // Zusatzfragen State
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [productOptions, setProductOptions] = useState({});
  const [showDessertUpsell, setShowDessertUpsell] = useState(false);
  const [selectedDips, setSelectedDips] = useState([]);
  
  // POS functions
  const addToCart = (product) => {
    // NEU: Check if product has modifier_groups (neue Modifier-Struktur)
    if (product.has_modifiers && product.modifier_groups && product.modifier_groups.length > 0) {
      // Open modifier dialog
      setSelectedProductForOptions(product);
      setShowProductOptions(true);
      return;
    }
    
    // Check if product needs the new ProductOptionsDialog (OHNE, Varianten, Extras)
    const needsNewOptionsDialog = 
      product.has_removable_ingredients ||
      product.has_variants ||
      product.has_extras;
    
    if (needsNewOptionsDialog) {
      // Open the new ProductOptionsDialog
      setSelectedProductForOptions(product);
      setShowProductOptions(true);
      return;
    }
    
    // Legacy: Prüfe ob alte Zusatzfragen nötig sind
    const needsLegacyOptions = 
      product.free_pizzabroetchen ||
      product.cheese_option ||
      product.free_dips > 0 ||
      product.paid_dip_option ||
      product.kiddy_box;
    
    if (needsLegacyOptions) {
      setPendingProduct(product);
      setProductOptions({});
      setSelectedDips([]);
      setShowOptionsModal(true);
    } else {
      // Direkt zum Warenkorb hinzufügen
      setCart(prev => {
        const existing = prev.find(item => item.name === product.name && JSON.stringify(item.options) === JSON.stringify({}));
        if (existing) return prev.map(item => item.name === product.name ? { ...item, quantity: item.quantity + 1 } : item);
        return [...prev, { ...product, quantity: 1, options: {} }];
      });
    }
  };
  
  // Callback für ProductOptionsDialog (OHNE, Varianten, Extras)
  const handleProductOptionsConfirm = (selection) => {
    // selection enthält: product, selectedVariant, removedIngredients, selectedExtras, totalPrice, modifiersText
    const { product, selectedVariant, removedIngredients, selectedExtras, totalPrice, modifiersText } = selection;
    
    // Erstelle Cart-Item
    const cartItem = {
      name: product.name,
      product_id: product.product_id,
      price: totalPrice,
      quantity: 1,
      vat_rate: product.vat_rate || product.tax_rate || 19,
      tag: product.product_type || 'food',
      // Optionen
      optionsText: modifiersText || '',
      variant: selectedVariant,
      removedIngredients: removedIngredients || [],
      extras: selectedExtras || [],
      // Für Kitchen Display
      modifications: {
        removed: (removedIngredients || []).map(i => i.name),
        added: (selectedExtras || []).map(e => e.name)
      }
    };
    
    setCart(prev => {
      // Erstelle eindeutigen Key basierend auf Optionen
      const optionsKey = JSON.stringify({
        variant: cartItem.variant?.variant_id,
        removed: cartItem.removedIngredients.map(i => i.ingredient_id),
        extras: cartItem.extras.map(e => e.extra_id)
      });
      
      const existing = prev.find(
        item => item.product_id === cartItem.product_id && 
        JSON.stringify({
          variant: item.variant?.variant_id,
          removed: (item.removedIngredients || []).map(i => i.ingredient_id),
          extras: (item.extras || []).map(e => e.extra_id)
        }) === optionsKey
      );
      
      if (existing) {
        return prev.map(item => 
          item === existing 
            ? { ...item, quantity: item.quantity + 1 } 
            : item
        );
      }
      
      return [...prev, cartItem];
    });
    
    setShowProductOptions(false);
    setSelectedProductForOptions(null);
  };

  // Callback für ModifierGroupsDialog (Neue Modifier-Struktur)
  const handleModifierGroupsConfirm = (selection) => {
    const { product, selections, totalPrice, modifiersText } = selection;
    
    // Erstelle Cart-Item mit Modifier-Selections
    const cartItem = {
      name: product.name,
      product_id: product.product_id,
      uid: product.uid,
      price: totalPrice,
      quantity: 1,
      vat_rate: product.vat_rate || product.tax_rate || 19,
      tag: product.tag || 'food',
      // Modifier-Selections
      modifiers: selections,
      modifiersText: modifiersText || '',
      // Für Kitchen Display
      modifications: {
        text: modifiersText
      }
    };
    
    setCart(prev => [...prev, cartItem]);
    
    setShowProductOptions(false);
    setSelectedProductForOptions(null);
    
    toast.success(`${product.name} zum Warenkorb hinzugefügt`);
  };

  
  // Produkt mit Optionen zum Warenkorb hinzufügen (Legacy)
  const addProductWithOptions = () => {
    if (!pendingProduct) return;
    
    let finalPrice = pendingProduct.price;
    let optionsText = [];
    
    // Käse-Aufpreis für Wraps
    if (productOptions.withCheese) {
      finalPrice += 1.50;
      optionsText.push('+ Käse überbacken');
    }
    
    // Gratis Pizzabrötchen
    if (productOptions.freePizzabroetchen) {
      optionsText.push('+ 3 Gratis Pizzabrötchen');
    }
    
    // Gratis Dips
    if (selectedDips.length > 0) {
      optionsText.push(`+ Dip: ${selectedDips.join(', ')}`);
    }
    
    // Kostenpflichtige Dips
    if (productOptions.paidDip) {
      const dipPrice = products?.options?.dip_prices?.[productOptions.paidDip] || 0.99;
      finalPrice += dipPrice;
      optionsText.push(`+ ${productOptions.paidDip} (+${dipPrice.toFixed(2)}€)`);
    }
    
    // Kiddy Box
    if (productOptions.kiddyOption) {
      optionsText.push(`Für: ${productOptions.kiddyOption}`);
    }
    
    const newItem = {
      ...pendingProduct,
      price: finalPrice,
      quantity: 1,
      options: productOptions,
      selectedDips: selectedDips,
      optionsText: optionsText.join(', ')
    };
    
    setCart(prev => [...prev, newItem]);
    setShowOptionsModal(false);
    setPendingProduct(null);
    setProductOptions({});
    setSelectedDips([]);
  };

  const updateCartQuantity = (productName, delta) => {
    setCart(prev => prev.map(item => {
      if (item.name === productName) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) return null;
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (productName) => setCart(prev => prev.filter(item => item.name !== productName));
  const clearCart = () => { setCart([]); setCustomerName(''); setCustomerPhone(''); setOrderTarget('counter'); setSelectedTableForOrder(null); };
  
  // Cart-Summe und Rabatt-Berechnung
  const cartSubtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  // Berechne anwendbare Rabatte basierend auf tenantSettings
  const calculateDiscount = () => {
    if (!tenantSettings?.discounts || tenantSettings.discounts.length === 0) {
      return { discount: 0, discountInfo: null };
    }
    
    let totalDiscount = 0;
    let appliedDiscount = null;
    
    for (const d of tenantSettings.discounts) {
      // Prüfe ob aktiv und auto_apply
      if (!d.is_active || !d.auto_apply) continue;
      
      // Prüfe applies_to
      if (d.applies_to !== 'all' && d.applies_to !== orderTarget) continue;
      
      // Prüfe Mindestbestellwert
      if (d.min_order_value && cartSubtotal < d.min_order_value) continue;
      
      // Berechne Rabatt
      let discountValue = 0;
      if (d.discount_type === 'percentage') {
        discountValue = cartSubtotal * (d.value / 100);
      } else {
        discountValue = d.value;
      }
      
      // Prüfe max_discount
      if (d.max_discount && discountValue > d.max_discount) {
        discountValue = d.max_discount;
      }
      
      if (discountValue > totalDiscount) {
        totalDiscount = discountValue;
        appliedDiscount = d;
      }
    }
    
    return { 
      discount: Math.round(totalDiscount * 100) / 100, 
      discountInfo: appliedDiscount 
    };
  };
  
  const { discount: appliedDiscount, discountInfo } = calculateDiscount();
  const cartTotal = cartSubtotal - appliedDiscount;

  const openPayment = (method) => {
    if (cart.length === 0) { toast.error('Warenkorb ist leer'); return; }
    if (orderTarget === 'pickup' && !customerName) { toast.error('Bitte Kundenname eingeben'); return; }
    
    // VEREINFACHT: Dessert-Upsell nur bei großen Bestellungen (>30€) anzeigen
    const shouldShowDessertUpsell = cartTotal > 30 && !cart.some(item => item.tag === 'Dessert');
    
    setPaymentMethod(method);
    setCashReceived('');
    
    if (shouldShowDessertUpsell) {
      setShowDessertUpsell(true);
    } else {
      // Direkt zur Zahlung
      setShowPaymentModal(true);
    }
  };
  
  const proceedToPayment = () => {
    setShowDessertUpsell(false);
    setShowPaymentModal(true);
  };
  
  const addDessertAndPay = (dessert) => {
    setCart(prev => [...prev, { ...dessert, quantity: 1, options: {}, tag: 'Dessert' }]);
    toast.success(`${dessert.name} hinzugefügt!`);
    setShowDessertUpsell(false);
    setShowPaymentModal(true);
  };

  const completePayment = async () => {
    if (paymentMethod === 'cash') {
      const received = parseFloat(cashReceived);
      if (isNaN(received) || received < cartTotal) { toast.error('Betrag zu klein'); return; }
    }
    setProcessing(true);
    try {
      // If order is for a table, add to table session
      if (orderTarget === 'table' && selectedTableForOrder) {
        const response = await fetch(`${API_BASE}/api/tables/${selectedTableForOrder}/order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: cart.map(item => ({ name: item.name, price_net: item.price / 1.19, vat_rate: 19, quantity: item.quantity })),
            course: 1
          })
        });
        if (response.ok) {
          toast.success(`Bestellung auf Tisch gebucht`);
          setShowPaymentModal(false);
          clearCart();
          fetchTables();
        }
      } else {
        // Regular POS order
        const response = await fetch(`${API_BASE}/api/cashx/kasse/pos/order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionToken}` },
          body: JSON.stringify({
            items: cart.map(item => {
              // Build options array for backend
              const options = [];
              
              // Add removed ingredients (OHNE)
              if (item.removedIngredients && item.removedIngredients.length > 0) {
                item.removedIngredients.forEach(ing => {
                  options.push({
                    type: 'ohne',
                    name: ing.name,
                    ingredient_id: ing.ingredient_id
                  });
                });
              }
              
              // Add extras
              if (item.extras && item.extras.length > 0) {
                item.extras.forEach(extra => {
                  options.push({
                    type: 'extra',
                    name: extra.name,
                    price: extra.price || 0,
                    extra_id: extra.extra_id
                  });
                });
              }
              
              return {
                name: item.name,
                quantity: item.quantity,
                price: item.price,
                tag: item.tag,
                options: options.length > 0 ? options : null,
                notes: item.optionsText || null
              };
            }),
            payment_method: paymentMethod,
            delivery_type: orderTarget,
            customer_name: customerName || null
          })
        });
        const data = await response.json();
        if (response.ok && data.success) {
          const changeBack = paymentMethod === 'cash' ? parseFloat(cashReceived) - cartTotal : 0;
          toast.success(changeBack > 0 ? `✅ Rückgeld: ${formatCurrency(changeBack)}` : '✅ Bezahlt!');
          
          // Print receipt automatically
          try {
            const printResponse = await fetch(`${API_BASE}/api/cashx/printers/print/document/kassenbon`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionToken}` },
              body: JSON.stringify({
                order_number: data.pos_number || data.order_id,
                items: cart.map(item => {
                  // Build modifiers text for receipt
                  const modifiers = [];
                  if (item.removedIngredients && item.removedIngredients.length > 0) {
                    item.removedIngredients.forEach(ing => {
                      modifiers.push(`OHNE ${ing.name}`);
                    });
                  }
                  if (item.extras && item.extras.length > 0) {
                    item.extras.forEach(extra => {
                      modifiers.push(`+ ${extra.name}`);
                    });
                  }
                  
                  return {
                    name: item.name,
                    quantity: item.quantity,
                    price: item.price,
                    modifiers: modifiers.join(', ') || null
                  };
                }),
                total: cartTotal,
                payment_method: paymentMethod,
                customer: customerName ? { name: customerName } : null
              })
            });
            const printData = await printResponse.json();
            if (printData.success) {
              toast.success('🖨️ Bon gedruckt');
            } else if (printData.message && !printData.message.includes('nicht verbunden')) {
              // Only show error if it's not about connection (user might not have print service)
              console.log('Print info:', printData.message);
            }
          } catch (printError) {
            console.log('Print service not available:', printError);
          }
          
          setShowPaymentModal(false);
          
          // FEATURE H: Nach Kartenzahlung Trinkgeld anbieten
          if (paymentMethod === 'card' && data.order_id) {
            setLastCompletedOrderId(data.order_id);
            setTimeout(() => setShowTipModal(true), 500);
          }
          
          clearCart();
          fetchStats();
          fetchOrders();
        }
      }
    } catch (error) {
      toast.error('Fehler');
    } finally {
      setProcessing(false);
    }
  };

  // Table functions
  const handleTableClick = async (table) => {
    setSelectedTable(table);
    if (table.status === 'occupied') {
      const session = await fetchTableSession(table.id);
      setTableSession(session);
    } else {
      setTableSession(null);
    }
    setShowTableModal(true);
  };

  const handleOpenTable = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/tables/${selectedTable.id}/open?guest_count=${guestCount}`, { method: 'POST' });
      if (response.ok) {
        toast.success(`Tisch ${selectedTable.table_number} geöffnet`);
        setShowTableModal(false);
        fetchTables();
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const handleTableCheckout = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/tables/${selectedTable.id}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cash_amount: tableSession?.totals?.total_gross || 0, card_amount: 0, tip_amount: 0, receipt_type: 'standard' })
      });
      if (response.ok) {
        toast.success(`Tisch ${selectedTable.table_number} abgerechnet`);
        setShowTableModal(false);
        setTableSession(null);
        fetchTables();
        fetchStats();
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  // Helpers
  const formatTime = (timestamp) => timestamp ? new Date(timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '-';
  const formatCurrency = (amount) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  
  // Get source configuration - handles various source formats
  const getSourceConfig = (source) => {
    if (!source) return SOURCE_CONFIG.unknown;
    const normalizedSource = source.toLowerCase().replace(/[^a-z]/g, '');
    
    // Direct match
    if (SOURCE_CONFIG[normalizedSource]) return SOURCE_CONFIG[normalizedSource];
    if (SOURCE_CONFIG[source]) return SOURCE_CONFIG[source];
    
    // Partial matches for common variations
    if (normalizedSource.includes('lieferando')) return SOURCE_CONFIG.lieferando;
    if (normalizedSource.includes('wolt')) return SOURCE_CONFIG.wolt;
    if (normalizedSource.includes('uber') || normalizedSource.includes('ubereats')) return SOURCE_CONFIG.ubereats;
    if (normalizedSource.includes('just') || normalizedSource.includes('justeat')) return SOURCE_CONFIG.justeat;
    if (normalizedSource.includes('deliveroo')) return SOURCE_CONFIG.deliveroo;
    if (normalizedSource.includes('foodora')) return SOURCE_CONFIG.foodora;
    if (normalizedSource.includes('mjam')) return SOURCE_CONFIG.mjam;
    if (normalizedSource.includes('website') || normalizedSource.includes('web')) return SOURCE_CONFIG.website;
    if (normalizedSource.includes('shop') || normalizedSource.includes('webshop')) return SOURCE_CONFIG.webshop;
    if (normalizedSource.includes('app')) return SOURCE_CONFIG.app;
    if (normalizedSource.includes('phone') || normalizedSource.includes('telefon') || normalizedSource.includes('tel')) return SOURCE_CONFIG.phone;
    if (normalizedSource.includes('pos') || normalizedSource.includes('kasse')) return SOURCE_CONFIG.pos;
    if (normalizedSource.includes('counter') || normalizedSource.includes('theke')) return SOURCE_CONFIG.counter;
    if (normalizedSource.includes('api') || normalizedSource.includes('online')) return SOURCE_CONFIG.online;
    
    return SOURCE_CONFIG.unknown;
  };
  
  const getStatusConfig = (status) => STATUS_COLORS[status] || STATUS_COLORS.new;

  const filteredProducts = selectedCategory && products[selectedCategory]
    ? products[selectedCategory].filter(p => searchQuery === '' || p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : [];

  // Categorize orders by type
  const pickupOrders = [...orders.new, ...orders.processing, ...orders.ready].filter(o => o.delivery_type === 'pickup' || o.delivery_type === 'counter');
  const deliveryOrders = [...orders.new, ...orders.processing, ...orders.ready].filter(o => o.delivery_type === 'delivery');
  const occupiedTables = tables.filter(t => t.status === 'occupied');
  const freeTables = tables.filter(t => t.status === 'free');

  // FEATURE C: Keyboard Shortcuts - Placed after filteredProducts definition
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ignore if typing in input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      // F1-F8: Categories
      if (e.key >= 'F1' && e.key <= 'F8') {
        e.preventDefault();
        const categoryIndex = parseInt(e.key.replace('F', '')) - 1;
        if (categories[categoryIndex]) {
          setSelectedCategory(categories[categoryIndex]);
          toast.info(`Kategorie: ${categories[categoryIndex]}`);
        }
      }
      
      // 1-9: Quick add first 9 products
      if (e.key >= '1' && e.key <= '9' && !e.ctrlKey && !e.altKey) {
        const productIndex = parseInt(e.key) - 1;
        if (filteredProducts[productIndex]) {
          addToCart(filteredProducts[productIndex]);
        }
      }
      
      // F9: Bar Payment
      if (e.key === 'F9') {
        e.preventDefault();
        if (cart.length > 0) {
          openPayment('cash');
        }
      }
      
      // F10: Card Payment
      if (e.key === 'F10') {
        e.preventDefault();
        if (cart.length > 0) {
          openPayment('card');
        }
      }
      
      // Escape: Clear search
      if (e.key === 'Escape') {
        setSearchQuery('');
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [categories, filteredProducts, cart]);


  // ============================================================================
  // LOGIN SCREEN
  // ============================================================================
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#0B1220] flex items-center justify-center p-4 relative overflow-hidden" data-testid="login-page">
        {/* Background decorations */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-72 h-72 bg-emerald-500/8 rounded-full blur-3xl" />
        </div>
        
        <Card className="w-full max-w-md border-slate-700/50 bg-[#111C32]/90 backdrop-blur-xl shadow-2xl shadow-black/40 relative">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 via-emerald-500 to-blue-500" />
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <img 
                src="/cash-x-logo.png" 
                alt="Cash-X Kassensystem" 
                className="h-24 w-auto object-contain"
              />
            </div>
            <CardTitle className="text-xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Kasse Login</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label className="text-slate-300 font-medium">E-Mail</Label>
                <Input 
                  type="email" 
                  value={loginEmail} 
                  onChange={(e) => setLoginEmail(e.target.value)} 
                  className="mt-1.5 h-12 bg-[#0B1220]/80 border-slate-700/70 text-white placeholder:text-slate-500 focus:border-teal-500/50 focus:ring-2 focus:ring-teal-500/20" 
                  required 
                  data-testid="login-email" 
                />
              </div>
              <div>
                <Label className="text-slate-300 font-medium">Passwort</Label>
                <Input 
                  type="password" 
                  value={loginPassword} 
                  onChange={(e) => setLoginPassword(e.target.value)} 
                  className="mt-1.5 h-12 bg-[#0B1220]/80 border-slate-700/70 text-white placeholder:text-slate-500 focus:border-teal-500/50 focus:ring-2 focus:ring-teal-500/20" 
                  required 
                  data-testid="login-password" 
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white font-bold shadow-lg shadow-teal-500/20 transition-all" 
                disabled={loginLoading} 
                data-testid="login-submit"
              >
                {loginLoading ? <RefreshCw className="animate-spin mr-2" size={18} /> : <LogIn className="mr-2" size={18} />}Anmelden
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============================================================================
  // STATION SELECTOR - After login, before main dashboard
  // ============================================================================
  if (!selectedStation) {
    return (
      <StationSelector
        businessName={account?.business_name}
        onSelectStation={handleStationSelect}
        onLogout={handleLogout}
      />
    );
  }

  // ============================================================================
  // KITCHEN STATIONS (Küche, Fritteuse, Salat, Pizza, Getränke, Grill)
  // ============================================================================
  const kitchenStations = ['kueche', 'fritteuse', 'salat', 'pizza', 'getraenke', 'grill'];
  if (kitchenStations.includes(selectedStation)) {
    return (
      <KitchenStation
        stationType={selectedStation}
        sessionToken={sessionToken}
        businessName={account?.business_name}
        onBack={handleBackToStationSelector}
      />
    );
  }

  // ============================================================================
  // DRIVER STATION
  // ============================================================================
  if (selectedStation === 'fahrer') {
    return (
      <DriverStation
        sessionToken={sessionToken}
        businessName={account?.business_name}
        onBack={handleBackToStationSelector}
      />
    );
  }

  // ============================================================================
  // PHONE STATION
  // ============================================================================
  if (selectedStation === 'telefon') {
    return (
      <PhoneStation
        sessionToken={sessionToken}
        businessName={account?.business_name}
        onBack={handleBackToStationSelector}
      />
    );
  }

  // ============================================================================
  // MAIN DASHBOARD (Hauptkasse) - selectedStation === 'hauptkasse'
  // ============================================================================
  
  // Handler for opening delivery/pickup modal from dashboard
  const handleOpenDeliveryPickup = (type) => {
    // Reset customer data
    setDeliveryCustomer({
      name: '', phone: '', street: '', houseNumber: '',
      postalCode: '', city: '', floor: '', notes: ''
    });
    setCustomerName('');
    setCustomerPhone('');
    setSelectedTableForOrder(null);
    
    // Set type and open modal
    if (type === 'delivery') {
      setDeliveryPickupType('delivery');
      setOrderTarget('delivery');
      setShowDeliveryPickupModal(true); // Modal öffnen statt direkt zur Kasse
      toast.info('Lieferbestellung', { description: 'Bitte Lieferadresse eingeben' });
    } else if (type === 'pickup') {
      setDeliveryPickupType('pickup');
      setOrderTarget('pickup');
      setShowDeliveryPickupModal(true); // Modal öffnen statt direkt zur Kasse
      toast.info('Abholung', { description: 'Bitte Kundendaten eingeben' });
    } else if (type === 'table') {
      setOrderTarget('table');
      setDashboardMode('pos'); // Bei Tisch direkt zur Kasse
      toast.info('Tisch-Bestellung', { description: 'Wählen Sie einen Tisch aus' });
    }
  };
  
  // New Dashboard Mode with Sidebar
  if (dashboardMode === 'dashboard') {
    return (
      <>
        <MainDashboard
          sessionToken={sessionToken}
          account={account}
          onLogout={handleLogout}
          onStationChange={handleBackToStationSelector}
          onOpenPrinters={() => setShowPrinterSettings(true)}
          onOpenIntegrations={() => setShowIntegrationsModal(true)}
          onSwitchToPOS={() => setDashboardMode('pos')}
          onOpenDeliveryPickup={handleOpenDeliveryPickup}
        />
        
        {/* Delivery/Pickup Modal - also available in dashboard mode */}
        <Dialog open={showDeliveryPickupModal} onOpenChange={setShowDeliveryPickupModal}>
          <DialogContent className="bg-[#12121a] border-gray-800 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                {!deliveryPickupType ? (
                  <>
                    <Package className="text-amber-400" />
                    Neue Bestellung
                  </>
                ) : deliveryPickupType === 'pickup' ? (
                  <>
                    <Store className="text-emerald-400" />
                    Abholung
                  </>
                ) : (
                  <>
                    <Truck className="text-blue-400" />
                    Lieferung
                  </>
                )}
              </DialogTitle>
            </DialogHeader>

            {/* Step 1: Choose Type */}
            {!deliveryPickupType && (
              <div className="grid grid-cols-2 gap-4 py-6">
                <button
                  onClick={() => {
                    setDeliveryPickupType('pickup');
                    setOrderTarget('pickup');
                  }}
                  className="p-8 rounded-xl border-2 border-gray-700 hover:border-emerald-500 hover:bg-emerald-500/10 transition-all group"
                >
                  <Store className="w-16 h-16 mx-auto mb-4 text-emerald-400 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-bold text-white">Abholung</h3>
                  <p className="text-gray-400 text-sm mt-2">Kunde holt selbst ab</p>
                </button>
                
                <button
                  onClick={() => setDeliveryPickupType('delivery')}
                  className="p-8 rounded-xl border-2 border-gray-700 hover:border-blue-500 hover:bg-blue-500/10 transition-all group"
                >
                  <Truck className="w-16 h-16 mx-auto mb-4 text-blue-400 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-bold text-white">Lieferung</h3>
                  <p className="text-gray-400 text-sm mt-2">Wir liefern zum Kunden</p>
                </button>
              </div>
            )}

            {/* Step 2a: Pickup - Just name and optional time */}
            {deliveryPickupType === 'pickup' && (
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-gray-400">Kundenname</Label>
                  <Input
                    value={deliveryCustomer.name}
                    onChange={(e) => setDeliveryCustomer({...deliveryCustomer, name: e.target.value})}
                    placeholder="Name des Kunden"
                    className="mt-1 bg-[#1a1a24] border-gray-700"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-400">Telefon (optional)</Label>
                  <Input
                    value={deliveryCustomer.phone}
                    onChange={(e) => setDeliveryCustomer({...deliveryCustomer, phone: e.target.value})}
                    placeholder="Telefonnummer"
                    className="mt-1 bg-[#1a1a24] border-gray-700"
                  />
                </div>

                {/* Scheduled Order Toggle */}
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Clock className="text-amber-400" size={18} />
                    <span>Vorbestellung (Zeitbestellung)</span>
                  </div>
                  <button
                    onClick={() => setIsScheduledOrder(!isScheduledOrder)}
                    className={`w-12 h-6 rounded-full transition-all ${isScheduledOrder ? 'bg-amber-500' : 'bg-gray-600'}`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white transition-all ${isScheduledOrder ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>

                {isScheduledOrder && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-gray-400">Datum</Label>
                      <Input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="mt-1 bg-[#1a1a24] border-gray-700"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400">Uhrzeit</Label>
                      <Input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className="mt-1 bg-[#1a1a24] border-gray-700"
                      />
                    </div>
                  </div>
                )}

                <Button
                  onClick={() => {
                    setCustomerName(deliveryCustomer.name);
                    setCustomerPhone(deliveryCustomer.phone);
                    setShowDeliveryPickupModal(false);
                    setDashboardMode('pos'); // Switch to POS mode
                  }}
                  className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white py-3"
                >
                  <ShoppingCart className="mr-2" size={18} />
                  Weiter zur Bestellung
                </Button>
              </div>
            )}

            {/* Step 2b: Delivery - Full customer details */}
            {deliveryPickupType === 'delivery' && (
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-gray-400">Kundenname *</Label>
                    <Input
                      value={deliveryCustomer.name}
                      onChange={(e) => setDeliveryCustomer({...deliveryCustomer, name: e.target.value})}
                      placeholder="Vor- und Nachname"
                      className="mt-1 bg-[#1a1a24] border-gray-700"
                      required
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label className="text-gray-400">Telefon *</Label>
                    <Input
                      value={deliveryCustomer.phone}
                      onChange={(e) => setDeliveryCustomer({...deliveryCustomer, phone: e.target.value})}
                      placeholder="Telefonnummer"
                      className="mt-1 bg-[#1a1a24] border-gray-700"
                      required
                    />
                  </div>

                  <div className="col-span-2 border-t border-gray-700 pt-3 mt-2">
                    <Label className="text-gray-400 flex items-center gap-2">
                      <MapPin size={14} /> Lieferadresse
                    </Label>
                  </div>

                  <div>
                    <Label className="text-gray-400">Straße *</Label>
                    <Input
                      value={deliveryCustomer.street}
                      onChange={(e) => setDeliveryCustomer({...deliveryCustomer, street: e.target.value})}
                      placeholder="Straßenname"
                      className="mt-1 bg-[#1a1a24] border-gray-700"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label className="text-gray-400">Hausnummer *</Label>
                    <Input
                      value={deliveryCustomer.houseNumber}
                      onChange={(e) => setDeliveryCustomer({...deliveryCustomer, houseNumber: e.target.value})}
                      placeholder="Nr."
                      className="mt-1 bg-[#1a1a24] border-gray-700"
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-gray-400">PLZ *</Label>
                    <Input
                      value={deliveryCustomer.postalCode}
                      onChange={(e) => setDeliveryCustomer({...deliveryCustomer, postalCode: e.target.value})}
                      placeholder="PLZ"
                      className="mt-1 bg-[#1a1a24] border-gray-700"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label className="text-gray-400">Stadt *</Label>
                    <Input
                      value={deliveryCustomer.city}
                      onChange={(e) => setDeliveryCustomer({...deliveryCustomer, city: e.target.value})}
                      placeholder="Stadt"
                      className="mt-1 bg-[#1a1a24] border-gray-700"
                      required
                    />
                  </div>

                  <div className="col-span-2">
                    <Label className="text-gray-400">Etage / Klingel (optional)</Label>
                    <Input
                      value={deliveryCustomer.floor}
                      onChange={(e) => setDeliveryCustomer({...deliveryCustomer, floor: e.target.value})}
                      placeholder="z.B. 3. OG links, Klingel Müller"
                      className="mt-1 bg-[#1a1a24] border-gray-700"
                    />
                  </div>

                  <div className="col-span-2">
                    <Label className="text-gray-400">Hinweise für Fahrer</Label>
                    <Input
                      value={deliveryCustomer.notes}
                      onChange={(e) => setDeliveryCustomer({...deliveryCustomer, notes: e.target.value})}
                      placeholder="z.B. Hintereingang, Hund im Garten..."
                      className="mt-1 bg-[#1a1a24] border-gray-700"
                    />
                  </div>
                </div>

                {/* Scheduled Order Toggle */}
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Clock className="text-amber-400" size={18} />
                    <span>Vorbestellung (Zeitbestellung)</span>
                  </div>
                  <button
                    onClick={() => setIsScheduledOrder(!isScheduledOrder)}
                    className={`w-12 h-6 rounded-full transition-all ${isScheduledOrder ? 'bg-amber-500' : 'bg-gray-600'}`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white transition-all ${isScheduledOrder ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>

                {isScheduledOrder && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-gray-400">Datum</Label>
                      <Input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="mt-1 bg-[#1a1a24] border-gray-700"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400">Gewünschte Uhrzeit</Label>
                      <Input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className="mt-1 bg-[#1a1a24] border-gray-700"
                      />
                    </div>
                  </div>
                )}

                <Button
                  onClick={() => {
                    if (!deliveryCustomer.name || !deliveryCustomer.phone || !deliveryCustomer.street || 
                        !deliveryCustomer.houseNumber || !deliveryCustomer.postalCode || !deliveryCustomer.city) {
                      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
                      return;
                    }
                    setCustomerName(deliveryCustomer.name);
                    setCustomerPhone(deliveryCustomer.phone);
                    setOrderTarget('delivery');
                    setShowDeliveryPickupModal(false);
                    setDashboardMode('pos'); // Switch to POS mode
                  }}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3"
                >
                  <ShoppingCart className="mr-2" size={18} />
                  Weiter zur Bestellung
                </Button>
              </div>
            )}

            {/* Back Button */}
            {deliveryPickupType && (
              <Button
                variant="ghost"
                onClick={() => setDeliveryPickupType(null)}
                className="mt-2"
              >
                ← Zurück zur Auswahl
              </Button>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Printer Settings Modal */}
        <Dialog open={showPrinterSettings} onOpenChange={setShowPrinterSettings}>
          <DialogContent className="bg-[#12121a] border-gray-800 text-white max-w-4xl max-h-[85vh] overflow-y-auto">
            <PrinterSettings 
              sessionToken={sessionToken} 
              onClose={() => setShowPrinterSettings(false)} 
            />
          </DialogContent>
        </Dialog>
      </>
    );
  }
  
  // Old Full POS Mode
  return (
    <div className={`h-screen bg-[#0a0a0f] text-white flex flex-col overflow-hidden ${getOrderTypeBorderColor()}`} data-testid="dashboard">
      {/* Header */}
      <header className="flex-shrink-0 bg-[#12121a] border-b border-gray-800 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="/cash-x-logo.png" 
                alt="Cash-X" 
                className="h-12 w-auto object-contain"
              />
            </div>
            <Badge className="bg-[#1a1a24] text-amber-300 border-amber-500/30"><Building2 size={12} className="mr-1" />{account?.business_name}</Badge>
            <Badge className={`${isOnline ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'}`}>
              {isOnline ? <><Wifi size={12} className="mr-1" />Online</> : <><WifiOff size={12} className="mr-1" />Offline</>}
            </Badge>
          </div>
          
          {/* Stats Bar */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 text-sm">
              <div className="flex items-center gap-1"><Bell size={14} className="text-amber-400" /><span className="font-bold text-amber-400">{orders.new.length}</span><span className="text-gray-500">Neu</span></div>
              <span className="text-gray-700">|</span>
              <div className="flex items-center gap-1"><ChefHat size={14} className="text-blue-400" /><span className="font-bold text-blue-400">{orders.processing.length}</span><span className="text-gray-500">Arbeit</span></div>
              {/* Tische-Anzeige nur wenn Tisch-Service aktiviert */}
              {tenantSettings?.feature_toggles?.table_service_enabled !== false && (
                <>
                  <span className="text-gray-700">|</span>
                  <div className="flex items-center gap-1"><UtensilsCrossed size={14} className="text-purple-400" /><span className="font-bold text-purple-400">{occupiedTables.length}/{tables.length}</span><span className="text-gray-500">Tische</span></div>
                </>
              )}
              <span className="text-gray-700">|</span>
              <div className="flex items-center gap-1"><Euro size={14} className="text-emerald-400" /><span className="font-bold text-emerald-400">{formatCurrency(stats?.stats?.completed?.total || 0)}</span></div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setDashboardMode('dashboard')} className="text-cyan-400 hover:text-cyan-300" title="Zurück zum Dashboard"><LayoutDashboard size={18} /></Button>
              <Button variant="ghost" size="sm" onClick={handleBackToStationSelector} className="text-purple-400 hover:text-purple-300" title="Station wechseln"><Monitor size={18} /></Button>
              <Button variant="ghost" size="sm" onClick={() => setShowPrinterSettings(true)} className="text-amber-400 hover:text-amber-300" title="Drucker-Einstellungen"><Printer size={18} /></Button>
              <Button variant="ghost" size="sm" onClick={() => setShowIntegrationsModal(true)} className="text-blue-400 hover:text-blue-300" title="API Integrationen"><Globe size={18} /></Button>
              <Button variant="ghost" size="sm" onClick={() => setSoundEnabled(!soundEnabled)} className={soundEnabled ? 'text-emerald-400' : 'text-gray-600'}>{soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}</Button>
              <Button variant="ghost" size="sm" onClick={() => { fetchOrders(); fetchTables(); }} className="text-gray-400"><RefreshCw size={18} /></Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-red-400"><LogOut size={18} /></Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Tab Navigation - Only on mobile/tablet */}
      <POSTabNavigation
        activeTab={mobileActiveTab}
        onTabChange={setMobileActiveTab}
        orderCounts={{
          tables: occupiedTables.length,
          pickup: pickupOrders.length,
          delivery: deliveryOrders.length
        }}
      />

      {/* Main Content - Responsive Layout */}
      <div className="flex-1 flex overflow-hidden pb-20 lg:pb-0">
        
        {/* LEFT SIDE - Live Orders Overview (Desktop) / Tab Content (Mobile) */}
        {/* NUR anzeigen bei Tisch-Modus oder wenn explizit gewünscht */}
        {orderTarget === 'table' && (
        <div className={`
          ${isMobile ? (mobileActiveTab === 'pos' ? 'hidden' : 'w-full') : 'w-1/2'} 
          border-r border-gray-800 flex flex-col overflow-hidden
        `}>
          <ScrollArea className="flex-1 p-4">
            
            {/* TISCHE Section - NUR bei Tisch-Bestellung anzeigen */}
            {orderTarget === 'table' && (!isMobile || mobileActiveTab === 'tables') && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <UtensilsCrossed size={16} className="text-purple-400" />
                  Tische
                  <Badge variant="outline" className="text-purple-400 border-purple-500/30 ml-2">{occupiedTables.length} besetzt</Badge>
                </h2>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {tables.slice(0, 10).map((table) => (
                  <div
                    key={table.id}
                    onClick={() => handleTableClick(table)}
                    className={`
                      p-3 rounded-lg cursor-pointer transition-all hover:scale-105 text-center min-h-[60px]
                      ${table.status === 'occupied' 
                        ? 'bg-purple-500/20 border border-purple-500/50 ring-1 ring-purple-500/30' 
                        : 'bg-[#1a1a24] border border-gray-800 hover:border-gray-700'}
                    `}
                    data-testid={`table-${table.table_number}`}
                  >
                    <div className="text-lg font-bold">{table.table_number}</div>
                    {table.status === 'occupied' && table.totals && (
                      <div className="text-sm text-emerald-400 font-semibold">{formatCurrency(table.totals.total_gross)}</div>
                    )}
                    {table.status === 'free' && <div className="text-xs text-gray-500">Frei</div>}
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* ABHOLER Section - Show on desktop always, or mobile when tab active */}
            {(!isMobile || mobileActiveTab === 'pickup') && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <Store size={16} className="text-emerald-400" />
                  Abholer & Theke
                  <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 ml-2">{pickupOrders.length} aktiv</Badge>
                </h2>
              </div>
              {pickupOrders.length === 0 ? (
                <div className="text-center py-6 text-gray-600 bg-[#12121a] rounded-lg border border-gray-800">
                  <Store size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Keine Abholer</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {pickupOrders.map((order) => {
                    const statusConfig = getStatusConfig(order.status);
                    const sourceConfig = getSourceConfig(order.source);
                    return (
                      <div
                        key={order.order_id}
                        onClick={() => { setSelectedOrder(order); setShowOrderDetailModal(true); }}
                        className={`p-3 rounded-lg bg-[#12121a] border border-gray-800 hover:border-gray-700 cursor-pointer transition-all flex items-center justify-between ${statusConfig.pulse ? 'animate-pulse' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${statusConfig.bg}`} />
                          <div>
                            <div className="font-bold text-sm">{order.zozo_order_number}</div>
                            <div className="text-xs text-gray-500">{order.customer?.name || 'Gast'}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const SourceIcon = sourceConfig.icon;
                            return (
                              <Badge className={`${sourceConfig.color} ${sourceConfig.textColor || 'text-white'} text-xs font-bold px-2 py-0.5 flex items-center gap-1`}>
                                {SourceIcon && <SourceIcon size={10} />}
                                {sourceConfig.label}
                              </Badge>
                            );
                          })()}
                          <div className="text-right">
                            <div className="font-bold text-sm text-emerald-400">{formatCurrency(order.total)}</div>
                            <div className="text-xs text-gray-500">{formatTime(order.received_at)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            {/* LIEFERUNG Section - Show on desktop always, or mobile when tab active */}
            {(!isMobile || mobileActiveTab === 'delivery') && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <Truck size={16} className="text-blue-400" />
                  Lieferungen
                  <Badge variant="outline" className="text-blue-400 border-blue-500/30 ml-2">{deliveryOrders.length} aktiv</Badge>
                </h2>
              </div>
              {deliveryOrders.length === 0 ? (
                <div className="text-center py-6 text-gray-600 bg-[#12121a] rounded-lg border border-gray-800">
                  <Truck size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Keine Lieferungen</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {deliveryOrders.map((order) => {
                    const statusConfig = getStatusConfig(order.status);
                    const sourceConfig = getSourceConfig(order.source);
                    const SourceIcon = sourceConfig.icon;
                    return (
                      <div
                        key={order.order_id}
                        onClick={() => { setSelectedOrder(order); setShowOrderDetailModal(true); }}
                        className={`p-3 rounded-lg bg-[#12121a] border border-gray-800 hover:border-gray-700 cursor-pointer transition-all ${statusConfig.pulse ? 'animate-pulse' : ''}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${statusConfig.bg}`} />
                            <span className="font-bold text-sm">{order.zozo_order_number}</span>
                            <Badge className={`${sourceConfig.color} ${sourceConfig.textColor || 'text-white'} text-xs font-bold px-2 py-0.5`}><SourceIcon size={10} className="mr-1" />{sourceConfig.label}</Badge>
                          </div>
                          <div className="font-bold text-emerald-400">{formatCurrency(order.total)}</div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center gap-1"><User size={12} />{order.customer?.name || 'Kunde'}</div>
                          <div className="flex items-center gap-1"><MapPin size={12} />{order.customer?.address?.substring(0, 30) || 'Adresse'}...</div>
                          <div className="flex items-center gap-1"><Clock size={12} />{formatTime(order.received_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            )}

          </ScrollArea>
        </div>
        )}

        {/* RIGHT SIDE - POS Kasse (Desktop full width / Mobile when tab=pos) */}
        <div className={`
          ${orderTarget === 'table' 
            ? (isMobile ? (mobileActiveTab === 'pos' ? 'w-full' : 'hidden') : 'w-1/2')
            : 'w-full'
          } 
          flex flex-col overflow-hidden bg-[#0d0d12]
        `}>
          
          {/* Order Target Selection */}
          <div className="flex-shrink-0 p-2 sm:p-3 bg-[#0B1220] border-b border-slate-700/50">
            <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
              <span className="text-xs text-slate-500 mr-1 sm:mr-2 font-medium hidden sm:block">Ziel:</span>
              {[
                { id: 'counter', label: 'Theke', shortLabel: 'Theke', icon: Store, color: 'amber', featureKey: 'counter_service_enabled' },
                { id: 'pickup', label: 'Abholer', shortLabel: 'Abhol', icon: Package, color: 'emerald', featureKey: 'pickup_enabled' },
                { id: 'table', label: 'Tisch', shortLabel: 'Tisch', icon: UtensilsCrossed, color: 'teal', featureKey: 'table_service_enabled' },
                { id: 'delivery', label: 'Lieferung', shortLabel: 'Liefer', icon: Truck, color: 'blue', featureKey: 'delivery_enabled' },
              ]
              // Filter based on tenant settings (feature toggles)
              .filter(target => {
                if (!tenantSettings?.feature_toggles) return true; // Show all if settings not loaded
                return tenantSettings.feature_toggles[target.featureKey] !== false;
              })
              .map(target => {
                const Icon = target.icon;
                const isActive = orderTarget === target.id;
                const colorClasses = {
                  amber: isActive ? 'bg-amber-500 text-black' : 'border-slate-700/60 text-slate-400 hover:text-amber-400 hover:border-amber-500/50',
                  emerald: isActive ? 'bg-emerald-500 text-white' : 'border-slate-700/60 text-slate-400 hover:text-emerald-400 hover:border-emerald-500/50',
                  teal: isActive ? 'bg-teal-500 text-white' : 'border-slate-700/60 text-slate-400 hover:text-teal-400 hover:border-teal-500/50',
                  blue: isActive ? 'bg-blue-500 text-white' : 'border-slate-700/60 text-slate-400 hover:text-blue-400 hover:border-blue-500/50',
                };
                return (
                  <Button
                    key={target.id}
                    size="sm"
                    variant={isActive ? 'default' : 'outline'}
                    onClick={() => setOrderTarget(target.id)}
                    className={`text-xs px-2 sm:px-3 py-2 transition-all min-h-[40px] ${colorClasses[target.color]}`}
                    data-testid={`target-${target.id}`}
                  >
                    <Icon size={14} className="mr-1 sm:mr-1.5" />
                    <span className="hidden xs:inline">{target.label}</span>
                    <span className="xs:hidden">{target.shortLabel}</span>
                  </Button>
                );
              })}
              
              {/* Table selector when table is selected */}
              {orderTarget === 'table' && (
                <select
                  value={selectedTableForOrder || ''}
                  onChange={(e) => setSelectedTableForOrder(e.target.value)}
                  className="ml-1 sm:ml-2 px-2 py-1 text-xs bg-[#1a1a24] border border-gray-700 rounded text-white min-h-[40px]"
                >
                  <option value="">Tisch wählen...</option>
                  {occupiedTables.map(t => <option key={t.id} value={t.id}>Tisch {t.table_number}</option>)}
                </select>
              )}
              
              {/* Customer name for pickup */}
              {orderTarget === 'pickup' && (
                <Input
                  placeholder="Kundenname..."
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="ml-2 h-8 w-32 text-xs bg-[#1a1a24] border-gray-700"
                />
              )}
            </div>
          </div>

          {/* Categories - MODERNIZED with better visual hierarchy */}
          <div className="flex-shrink-0 p-3 bg-[#0E1628]/90 backdrop-blur-sm border-b border-slate-700/50 overflow-x-auto">
            <div className="flex gap-2">
              {categories.map(cat => {
                const CatIcon = CATEGORY_ICONS[cat] || UtensilsCrossed;
                const isActive = selectedCategory === cat;
                return (
                  <Button
                    key={cat}
                    size="sm"
                    variant={isActive ? 'default' : 'ghost'}
                    onClick={() => setSelectedCategory(cat)}
                    className={`
                      text-sm whitespace-nowrap px-4 py-2.5 rounded-lg transition-all duration-200 min-h-[44px]
                      font-medium
                      ${isActive 
                        ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-lg shadow-teal-500/25 scale-105' 
                        : 'text-slate-300 hover:text-white hover:bg-slate-700/60 hover:scale-102 border border-slate-700/50'
                      }
                      active:scale-[0.98]
                    `}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <CatIcon size={16} className={`mr-2 ${isActive ? '' : 'opacity-70'}`} />
                    {cat}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Products Grid - MODERNIZED with better touch targets */}
          <div className="flex-1 overflow-hidden flex">
            <ScrollArea className="flex-1 p-3 sm:p-4">
              {/* FEATURE A & B: Favoriten + Quick Reorder */}
              <FavoritesPanel onAddToCart={(fav) => {
                // Add favorite config directly to cart
                setCart(prev => [...prev, {
                  name: fav.product_name,
                  product_id: fav.product_id,
                  price: fav.total_price,
                  quantity: 1,
                  modifiers: fav.modifiers,
                  from_favorite: true
                }]);
              }} />
              
              <QuickReorderPanel onAddToCart={(item) => {
                // Add reorder item to cart
                setCart(prev => {
                  const existing = prev.find(i => i.name === item.name);
                  if (existing) {
                    return prev.map(i => i.name === item.name ? {...i, quantity: i.quantity + item.quantity} : i);
                  }
                  return [...prev, item];
                });
              }} />
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {filteredProducts.map((product, idx) => (
                  <ProductCard
                    key={idx}
                    product={product}
                    onClick={() => addToCart(product)}
                    showImages={productImageSettings.enabled && productImageSettings.show_on_kasse}
                    imageSize={productImageSettings.image_size}
                    dataTestId={`product-${idx}`}
                  />
                ))}
              </div>
            </ScrollArea>

            {/* Cart - Hidden on mobile/tablet, shown via drawer instead */}
            <div className="hidden lg:flex w-72 bg-[#111C32]/90 backdrop-blur-xl border-l border-slate-700/50 flex-col">
              <div className="p-4 border-b border-slate-700/50 flex items-center justify-between bg-gradient-to-r from-[#0B1220] to-[#111C32]">
                <span className="font-semibold text-base flex items-center gap-2 text-white"><ShoppingCart size={18} className="text-teal-400" />Warenkorb</span>
                {cart.length > 0 && <Button variant="ghost" size="sm" onClick={clearCart} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2 text-xs"><X size={14} className="mr-1" />Leeren</Button>}
              </div>
              
              <ScrollArea className="flex-1 p-3">
                {cart.length === 0 ? (
                  <div className="text-center py-12 text-slate-500"><ShoppingCart size={40} className="mx-auto mb-3 opacity-30" /><p className="text-sm">Warenkorb ist leer</p></div>
                ) : (
                  <div className="space-y-2">
                    {cart.map((item, idx) => (
                      <div key={idx} className="p-3 bg-[#0B1220]/60 rounded-xl border border-slate-700/40 hover:border-slate-600/60 transition-colors">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-medium line-clamp-1 text-white">{item.name}</span>
                          <Button variant="ghost" size="sm" onClick={() => removeFromCart(item.name)} className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"><X size={12} /></Button>
                        </div>
                        {/* Optionen anzeigen */}
                        {item.optionsText && (
                          <div className="text-[11px] text-teal-400 mb-1.5 line-clamp-2">{item.optionsText}</div>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <Button variant="outline" size="sm" onClick={() => updateCartQuantity(item.name, -1)} className="h-7 w-7 p-0 border-slate-600/60 bg-slate-800/50 hover:bg-slate-700/50 text-white"><Minus size={12} /></Button>
                            <span className="w-7 text-center text-sm font-bold text-white">{item.quantity}</span>
                            <Button variant="outline" size="sm" onClick={() => updateCartQuantity(item.name, 1)} className="h-7 w-7 p-0 border-slate-600/60 bg-slate-800/50 hover:bg-slate-700/50 text-white"><Plus size={12} /></Button>
                          </div>
                          <span className="text-teal-400 font-bold text-sm">{formatCurrency(item.price * item.quantity)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* Cart Total & Payment */}
              {cart.length > 0 && (
                <div className="p-4 border-t border-slate-700/50 bg-gradient-to-t from-[#0B1220] to-transparent">
                  {/* Subtotal */}
                  {appliedDiscount > 0 && (
                    <div className="flex justify-between text-sm text-slate-400 mb-1">
                      <span>Zwischensumme</span>
                      <span>{formatCurrency(cartSubtotal)}</span>
                    </div>
                  )}
                  
                  {/* Discount Line */}
                  {appliedDiscount > 0 && discountInfo && (
                    <div className="flex justify-between text-sm mb-2 text-emerald-400">
                      <span className="flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-emerald-400"></span>
                        {discountInfo.name} ({discountInfo.value}%)
                      </span>
                      <span>-{formatCurrency(appliedDiscount)}</span>
                    </div>
                  )}
                  
                  {/* Total */}
                  <div className="flex justify-between text-xl font-bold mb-4">
                    <span className="text-white">Gesamt</span>
                    <span className="text-teal-400">{formatCurrency(cartTotal)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Button onClick={() => openPayment('cash')} className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-semibold shadow-lg shadow-emerald-500/20" data-testid="pay-cash"><Banknote size={16} className="mr-2" />Bar</Button>
                    <Button onClick={() => openPayment('card')} className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-semibold shadow-lg shadow-blue-500/20" data-testid="pay-card"><CreditCard size={16} className="mr-2" />Karte</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Bar - Fixed at bottom on mobile/tablet */}
      <MobileBottomBar
        cart={cart}
        cartTotal={cartTotal}
        onOpenCart={() => setCartDrawerOpen(true)}
        onPayCash={() => openPayment('cash')}
        onPayCard={() => openPayment('card')}
        formatCurrency={formatCurrency}
      />

      {/* Cart Drawer - Slide-in cart for mobile/tablet */}
      <CartDrawer
        open={cartDrawerOpen}
        onOpenChange={setCartDrawerOpen}
        cart={cart}
        cartTotal={cartTotal}
        onUpdateQuantity={updateCartQuantity}
        onRemoveItem={removeFromCart}
        onClearCart={clearCart}
        onPayCash={() => openPayment('cash')}
        onPayCard={() => openPayment('card')}
        formatCurrency={formatCurrency}
      />

      {/* ProductOptions Dialog (OHNE, Varianten, Extras) - Legacy */}
      <ProductOptionsDialog
        isOpen={showProductOptions && selectedProductForOptions && !selectedProductForOptions?.modifier_groups?.length}
        onClose={() => {
          setShowProductOptions(false);
          setSelectedProductForOptions(null);
        }}
        product={selectedProductForOptions}
        onConfirm={handleProductOptionsConfirm}
      />

      {/* ModifierGroups Dialog (Neue Modifier-Struktur) */}
      <ModifierGroupsDialog
        isOpen={showProductOptions && selectedProductForOptions && selectedProductForOptions?.modifier_groups?.length > 0}
        onClose={() => {
          setShowProductOptions(false);
          setSelectedProductForOptions(null);
        }}
        product={selectedProductForOptions}
        onConfirm={handleModifierGroupsConfirm}
      />

      {/* ================================================================ */}
      {/* MODALS */}
      {/* ================================================================ */}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => !processing && setShowPaymentModal(false)}>
          <Card className="w-[420px] max-w-[95vw] bg-[#111C32]/95 backdrop-blur-xl border-slate-700/60 shadow-2xl shadow-black/50" onClick={e => e.stopPropagation()}>
            <div className={`h-1 ${paymentMethod === 'cash' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-gradient-to-r from-blue-500 to-cyan-500'}`} />
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-3 text-xl">
                {paymentMethod === 'cash' ? (
                  <><div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center"><Banknote className="text-emerald-400" size={20} /></div><span className="text-white">Barzahlung</span></>
                ) : (
                  <><div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center"><CreditCard className="text-blue-400" size={20} /></div><span className="text-white">Kartenzahlung</span></>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-5 bg-[#0B1220] rounded-xl mb-5 border border-slate-700/40">
                {/* Rabatt-Anzeige im Payment Modal */}
                {appliedDiscount > 0 && discountInfo && (
                  <div className="mb-3 pb-3 border-b border-slate-700/50">
                    <div className="flex justify-between text-sm text-slate-400">
                      <span>Zwischensumme</span>
                      <span>{formatCurrency(cartSubtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-emerald-400 mt-1">
                      <span className="flex items-center gap-1">
                        <Check size={12} />
                        {discountInfo.name}
                      </span>
                      <span>-{formatCurrency(appliedDiscount)}</span>
                    </div>
                  </div>
                )}
                <div className="text-center">
                  <div className="text-sm text-slate-400 mb-1">Zu zahlen</div>
                  <div className="text-4xl font-bold text-teal-400" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{formatCurrency(cartTotal)}</div>
                  {orderTarget !== 'counter' && <div className="text-xs text-slate-500 mt-2">{orderTarget === 'pickup' ? `Abholer: ${customerName}` : orderTarget === 'table' ? 'Auf Tisch buchen' : 'Lieferung'}</div>}
                </div>
              </div>
              {paymentMethod === 'cash' && (
                <>
                  <Input 
                    type="number" 
                    value={cashReceived} 
                    onChange={(e) => setCashReceived(e.target.value)} 
                    placeholder="Betrag erhalten" 
                    className="text-2xl text-center bg-slate-900 border-2 border-teal-500/50 mb-3 text-white placeholder:text-slate-500 font-bold h-14 focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30" 
                    autoFocus 
                    data-testid="cash-amount-input"
                  />
                  <div className="grid grid-cols-5 gap-2 mb-4">
                    {[5, 10, 20, 50, 100].map(amt => (
                      <Button 
                        key={amt} 
                        variant="outline" 
                        onClick={() => setCashReceived(amt.toString())} 
                        className="border-slate-600/60 bg-slate-800/50 hover:bg-teal-500/10 hover:border-teal-500/50 text-white font-semibold transition-all min-h-[44px]"
                      >
                        {amt}€
                      </Button>
                    ))}
                  </div>
                  {cashReceived && parseFloat(cashReceived) >= cartTotal && (
                    <div className="p-4 bg-emerald-500/15 rounded-xl text-center border border-emerald-500/30 mb-4">
                      <span className="text-sm text-slate-300">Rückgeld: </span>
                      <span className="text-3xl font-bold text-emerald-400">{formatCurrency(parseFloat(cashReceived) - cartTotal)}</span>
                    </div>
                  )}
                </>
              )}
              {paymentMethod === 'card' && (
                <div className="text-center py-8">
                  <div className="w-20 h-20 rounded-2xl bg-blue-500/15 flex items-center justify-center mx-auto mb-4">
                    <CreditCard size={40} className="text-blue-400" />
                  </div>
                  <p className="text-slate-400 text-base">Bitte Karte am Terminal</p>
                </div>
              )}
              
              {/* FEATURE G: Split Bill Button */}
              {cart.length > 1 && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setShowSplitBillModal(true);
                  }}
                  className="w-full mb-3 border-blue-500/40 text-blue-400 hover:bg-blue-500/10 hover:border-blue-400"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Rechnung teilen
                </Button>
              )}
              
              <div className="flex gap-3 mt-4">
                <Button variant="outline" onClick={() => setShowPaymentModal(false)} className="flex-1 border-slate-600/60 text-slate-300 hover:bg-slate-800/50" disabled={processing}>Abbrechen</Button>
                <Button 
                  onClick={completePayment} 
                  className={`flex-1 font-semibold ${paymentMethod === 'cash' ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400' : 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400'} text-white shadow-lg`} 
                  disabled={processing || (paymentMethod === 'cash' && (!cashReceived || parseFloat(cashReceived) < cartTotal))}
                >
                  {processing ? <RefreshCw className="animate-spin mr-2" size={16} /> : <Check size={16} className="mr-2" />}Bestätigen
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Produkt-Optionen Modal (Zusatzfragen) */}
      <Dialog open={showOptionsModal} onOpenChange={setShowOptionsModal}>
        <DialogContent className="bg-[#12121a] border-gray-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UtensilsCrossed className="text-amber-400" />
              {pendingProduct?.name}
            </DialogTitle>
          </DialogHeader>
          
          {pendingProduct && (
            <div className="space-y-4 py-2">
              {/* Gratis Pizzabrötchen für Salat/Suppe/Pasta */}
              {pendingProduct.free_pizzabroetchen && (
                <div className="p-4 bg-[#1a1a24] rounded-lg border border-amber-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">🥯</span>
                    <div>
                      <div className="font-semibold text-amber-400">3 Gratis Pizzabrötchen</div>
                      <div className="text-xs text-gray-500">Möchten Sie die 3 gratis Pizzabrötchen dazu?</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Button onClick={() => setProductOptions(p => ({ ...p, freePizzabroetchen: true }))} className={`py-6 ${productOptions.freePizzabroetchen ? 'bg-emerald-600 ring-2 ring-emerald-400' : 'bg-emerald-600/30'}`}>
                      <div className="text-center"><Check size={24} className="mx-auto mb-1" /><div className="font-bold">JA BITTE</div><div className="text-xs opacity-70">GRATIS</div></div>
                    </Button>
                    <Button onClick={() => setProductOptions(p => ({ ...p, freePizzabroetchen: false }))} variant="outline" className={`py-6 border-gray-700 ${productOptions.freePizzabroetchen === false ? 'ring-2 ring-gray-400' : ''}`}>
                      <div className="text-center"><X size={24} className="mx-auto mb-1" /><div className="font-bold">NEIN DANKE</div></div>
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Käse-Option für Wraps */}
              {pendingProduct.cheese_option && (
                <div className="p-4 bg-[#1a1a24] rounded-lg border border-amber-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">🧀</span>
                    <div>
                      <div className="font-semibold text-amber-400">Mit Käse überbacken?</div>
                      <div className="text-xs text-gray-500">Soll der Wrap mit Käse überbacken werden?</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Button onClick={() => setProductOptions(p => ({ ...p, withCheese: true }))} className={`py-6 ${productOptions.withCheese ? 'bg-amber-600 ring-2 ring-amber-400' : 'bg-amber-600/30'}`}>
                      <div className="text-center"><span className="text-2xl">🧀</span><div className="font-bold mt-1">JA, MIT KÄSE</div><div className="text-xs text-emerald-300">+1,50 €</div></div>
                    </Button>
                    <Button onClick={() => setProductOptions(p => ({ ...p, withCheese: false }))} variant="outline" className={`py-6 border-gray-700 ${productOptions.withCheese === false ? 'ring-2 ring-gray-400' : ''}`}>
                      <div className="text-center"><X size={24} className="mx-auto mb-1" /><div className="font-bold">OHNE KÄSE</div><div className="text-xs opacity-70">(Standard)</div></div>
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Gratis Dips für Fingerfood */}
              {pendingProduct.free_dips > 0 && (
                <div className="p-4 bg-[#1a1a24] rounded-lg border border-emerald-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">🥫</span>
                    <div>
                      <div className="font-semibold text-emerald-400">
                        {pendingProduct.free_dips === 1 ? 'Welchen Dip möchten Sie? (1x GRATIS)' : 'Welche 2 Dips möchten Sie? (2x GRATIS)'}
                      </div>
                      <div className="text-xs text-gray-500">{pendingProduct.free_dips === 1 ? 'Ein Dip ist gratis.' : 'Beide Dips sind gratis.'}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {['Ketchup', 'Mayonnaise', 'BBQ Sauce', 'Sweet Chili', 'Knoblauch', 'Chili Sauce', 'Curry Sauce'].map(dip => (
                      <Button 
                        key={dip} 
                        variant="outline"
                        onClick={() => {
                          if (selectedDips.includes(dip)) {
                            setSelectedDips(prev => prev.filter(d => d !== dip));
                          } else if (selectedDips.length < pendingProduct.free_dips) {
                            setSelectedDips(prev => [...prev, dip]);
                          }
                        }}
                        className={`text-xs py-3 ${selectedDips.includes(dip) ? 'bg-emerald-600 border-emerald-400 text-white' : 'border-gray-700'}`}
                        disabled={!selectedDips.includes(dip) && selectedDips.length >= pendingProduct.free_dips}
                      >
                        {dip}
                        {selectedDips.includes(dip) && <Check size={12} className="ml-1" />}
                      </Button>
                    ))}
                    <Button variant="outline" onClick={() => setSelectedDips([])} className={`text-xs py-3 border-gray-700 ${selectedDips.length === 0 ? 'ring-2 ring-gray-400' : ''}`}>
                      <X size={12} className="mr-1" />Kein Dip
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Kostenpflichtige Dips für Pizzabrötchen */}
              {pendingProduct.paid_dip_option && (
                <div className="p-4 bg-[#1a1a24] rounded-lg border border-blue-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">🥫</span>
                    <div>
                      <div className="font-semibold text-blue-400">Möchten Sie einen Dip dazu?</div>
                      <div className="text-xs text-gray-500">Dips sind kostenpflichtig</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { name: 'Ketchup', price: 0.99 },
                      { name: 'Mayo', price: 0.99 },
                      { name: 'Sweet Chili', price: 0.69 },
                      { name: 'BBQ Sauce', price: 1.19 },
                      { name: 'Knoblauch', price: 1.19 },
                      { name: 'Chili', price: 1.19 }
                    ].map(dip => (
                      <Button 
                        key={dip.name} 
                        variant="outline"
                        onClick={() => setProductOptions(p => ({ ...p, paidDip: productOptions.paidDip === dip.name ? null : dip.name }))}
                        className={`text-xs py-3 ${productOptions.paidDip === dip.name ? 'bg-blue-600 border-blue-400 text-white' : 'border-gray-700'}`}
                      >
                        <div className="text-center">
                          <div>{dip.name}</div>
                          <div className="text-[10px] text-amber-400">{dip.price.toFixed(2)}€</div>
                        </div>
                      </Button>
                    ))}
                  </div>
                  <Button variant="outline" onClick={() => setProductOptions(p => ({ ...p, paidDip: null }))} className={`w-full mt-2 text-xs border-gray-700 ${!productOptions.paidDip ? 'ring-2 ring-gray-400' : ''}`}>
                    <X size={12} className="mr-1" />Kein Dip (GRATIS)
                  </Button>
                </div>
              )}
              
              {/* Kiddy Box Junge/Mädchen */}
              {pendingProduct.kiddy_box && (
                <div className="p-4 bg-[#1a1a24] rounded-lg border border-pink-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">🎁</span>
                    <div>
                      <div className="font-semibold text-pink-400">Kiddy Box - Für wen ist sie?</div>
                      <div className="text-xs text-gray-500">Ist die Kiddy Box für einen Jungen oder ein Mädchen?</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Button onClick={() => setProductOptions(p => ({ ...p, kiddyOption: 'Junge' }))} className={`py-8 ${productOptions.kiddyOption === 'Junge' ? 'bg-blue-600 ring-2 ring-blue-400' : 'bg-blue-600/30'}`}>
                      <div className="text-center"><span className="text-4xl">👦</span><div className="font-bold mt-2">JUNGE</div></div>
                    </Button>
                    <Button onClick={() => setProductOptions(p => ({ ...p, kiddyOption: 'Mädchen' }))} className={`py-8 ${productOptions.kiddyOption === 'Mädchen' ? 'bg-pink-600 ring-2 ring-pink-400' : 'bg-pink-600/30'}`}>
                      <div className="text-center"><span className="text-4xl">👧</span><div className="font-bold mt-2">MÄDCHEN</div></div>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowOptionsModal(false); setPendingProduct(null); }} className="border-gray-700">Abbrechen</Button>
            <Button onClick={addProductWithOptions} className="bg-amber-500 hover:bg-amber-600 text-black">
              <ShoppingCart size={16} className="mr-2" />Zum Warenkorb ({formatCurrency(pendingProduct?.price + (productOptions.withCheese ? 1.50 : 0) + (productOptions.paidDip ? 0.99 : 0))})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dessert Upsell Modal */}
      <Dialog open={showDessertUpsell} onOpenChange={setShowDessertUpsell}>
        <DialogContent className="bg-[#12121a] border-gray-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <span className="text-2xl">🍰</span>
              Darf es noch etwas Süßes sein?
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-400 text-sm mb-4">Darf es noch etwas Süßes dazu sein?</p>
            <div className="grid grid-cols-3 gap-3">
              {[
                { name: 'American ZOZO Brownie', price: 3.49, emoji: '🍫' },
                { name: 'Miss Chocolic Muffin', price: 3.49, emoji: '🧁' },
                { name: 'Tiramizozo', price: 3.49, emoji: '🍮' }
              ].map(dessert => (
                <Button
                  key={dessert.name}
                  variant="outline"
                  onClick={() => addDessertAndPay(dessert)}
                  className="h-auto py-4 flex flex-col items-center border-gray-700 hover:border-amber-500 hover:bg-amber-500/10"
                >
                  <span className="text-3xl mb-2">{dessert.emoji}</span>
                  <div className="font-medium text-xs text-center">{dessert.name}</div>
                  <div className="text-amber-400 font-bold mt-1">{formatCurrency(dessert.price)}</div>
                </Button>
              ))}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={proceedToPayment} className="w-full border-gray-700 py-4">
              <X size={16} className="mr-2" />Nein danke, weiter zur Kasse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Detail Modal */}
      <Dialog open={showOrderDetailModal} onOpenChange={setShowOrderDetailModal}>
        <DialogContent className="bg-[#12121a] border-gray-800 max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Package className="text-amber-400" />Bestellung {selectedOrder?.zozo_order_number}</DialogTitle></DialogHeader>
          {selectedOrder && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Badge className={`${getSourceConfig(selectedOrder.source).color} text-white`}>{getSourceConfig(selectedOrder.source).label}</Badge>
                <Badge className={`${getStatusConfig(selectedOrder.status).bg} text-white`}>{getStatusConfig(selectedOrder.status).label}</Badge>
              </div>
              {selectedOrder.customer && (
                <div className="p-3 bg-[#1a1a24] rounded-lg mb-4 text-sm">
                  <div className="flex items-center gap-2 mb-1"><User size={14} className="text-gray-400" />{selectedOrder.customer.name || 'Gast'}</div>
                  {selectedOrder.customer.phone && <div className="flex items-center gap-2 mb-1"><Phone size={14} className="text-gray-400" />{selectedOrder.customer.phone}</div>}
                  {selectedOrder.customer.address && <div className="flex items-center gap-2"><MapPin size={14} className="text-gray-400" />{selectedOrder.customer.address}</div>}
                </div>
              )}
              <div className="space-y-2 mb-4">
                {selectedOrder.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm"><span>{item.quantity}x {item.name}</span><span className="text-emerald-400">{formatCurrency(item.price || item.total)}</span></div>
                ))}
              </div>
              <Separator className="my-3 bg-gray-800" />
              <div className="flex justify-between text-lg font-bold"><span>Gesamt</span><span className="text-amber-400">{formatCurrency(selectedOrder.total)}</span></div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {selectedOrder?.status === 'new' && <Button onClick={() => { updateOrderStatus(selectedOrder.order_id, 'processing'); setShowOrderDetailModal(false); }} className="bg-blue-600"><ChefHat size={16} className="mr-1" />Bearbeiten</Button>}
            {selectedOrder?.status === 'processing' && <Button onClick={() => { updateOrderStatus(selectedOrder.order_id, 'ready'); setShowOrderDetailModal(false); }} className="bg-emerald-600"><CheckCircle2 size={16} className="mr-1" />Bereit</Button>}
            {selectedOrder?.status === 'ready' && <Button onClick={() => { updateOrderStatus(selectedOrder.order_id, 'completed'); setShowOrderDetailModal(false); }} className="bg-purple-600"><Check size={16} className="mr-1" />Abgeschlossen</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table Modal */}
      <Dialog open={showTableModal} onOpenChange={setShowTableModal}>
        <DialogContent className="bg-[#12121a] border-gray-800">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UtensilsCrossed className="text-purple-400" />Tisch {selectedTable?.table_number}</DialogTitle></DialogHeader>
          {selectedTable?.status === 'free' ? (
            <div className="py-6">
              <p className="text-center text-gray-400 mb-4">Tisch öffnen für wie viele Gäste?</p>
              <div className="flex items-center justify-center gap-4 mb-6">
                <Button variant="outline" size="icon" onClick={() => setGuestCount(Math.max(1, guestCount - 1))} className="border-gray-700">-</Button>
                <span className="text-4xl font-bold w-16 text-center">{guestCount}</span>
                <Button variant="outline" size="icon" onClick={() => setGuestCount(guestCount + 1)} className="border-gray-700">+</Button>
              </div>
              <Button onClick={handleOpenTable} className="w-full bg-emerald-600 hover:bg-emerald-700"><UserPlus size={16} className="mr-2" />Tisch öffnen</Button>
            </div>
          ) : tableSession ? (
            <div>
              <div className="flex items-center gap-4 mb-4 text-sm text-gray-400">
                <span><Users size={14} className="inline mr-1" />{tableSession.session?.guest_count} Gäste</span>
                <span><Clock size={14} className="inline mr-1" />{formatTime(tableSession.session?.opened_at)}</span>
              </div>
              {tableSession.session?.orders?.length > 0 && (
                <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                  {tableSession.session.orders.map((order, idx) => (
                    <div key={idx} className="p-2 bg-[#1a1a24] rounded">
                      {order.items?.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm"><span>{item.quantity || 1}x {item.name}</span><span className="text-emerald-400">{formatCurrency((item.price_net || 0) * (item.quantity || 1) * 1.19)}</span></div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              {tableSession.totals && (
                <div className="p-4 bg-[#1a1a24] rounded-lg mb-4">
                  <div className="flex justify-between text-sm text-gray-400"><span>Netto</span><span>{formatCurrency(tableSession.totals.total_net)}</span></div>
                  <div className="flex justify-between text-sm text-gray-400"><span>MwSt</span><span>{formatCurrency(tableSession.totals.total_vat)}</span></div>
                  <Separator className="my-2 bg-gray-700" />
                  <div className="flex justify-between text-lg font-bold"><span>Gesamt</span><span className="text-emerald-400">{formatCurrency(tableSession.totals.total_gross)}</span></div>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={() => { setShowTableModal(false); setOrderTarget('table'); setSelectedTableForOrder(selectedTable.id); }} className="flex-1 bg-purple-600"><Plus size={16} className="mr-1" />Bestellung</Button>
                <Button onClick={handleTableCheckout} className="flex-1 bg-emerald-600" disabled={!tableSession.totals?.total_gross}><Receipt size={16} className="mr-1" />Abrechnen</Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">Laden...</div>
          )}
        </DialogContent>
      </Dialog>

      {/* API Integrationen Modal */}
      <Dialog open={showIntegrationsModal} onOpenChange={setShowIntegrationsModal}>
        <DialogContent className="bg-[#12121a] border-gray-800 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Globe className="text-blue-400" />
              API Integrationen & Schnittstellen
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Webhook URL & Test */}
            <div className="p-4 bg-[#1a1a24] rounded-lg border border-gray-800">
              <h3 className="font-semibold text-amber-400 mb-2 flex items-center gap-2">
                <Key size={16} />
                Ihre API-Zugangsdaten
              </h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-gray-400 text-xs">Webhook URL (für alle Portale)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input readOnly value={`${window.location.origin}/api/cashx/orders`} className="bg-[#0a0a0f] border-gray-700 text-sm font-mono" />
                    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/api/cashx/orders`); toast.success('URL kopiert!'); }} className="border-gray-700">Kopieren</Button>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">API-Key</Label>
                  <div className="flex gap-2 mt-1">
                    <Input readOnly value="zozo-3a831ac1-30028892-bea62b66" className="bg-[#0a0a0f] border-gray-700 text-sm font-mono" />
                    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText('zozo-3a831ac1-30028892-bea62b66'); toast.success('API-Key kopiert!'); }} className="border-gray-700">Kopieren</Button>
                  </div>
                </div>
                
                {/* Webhook Test Button */}
                <div className="pt-3 border-t border-gray-800">
                  <div className="flex items-center gap-3">
                    <Button 
                      onClick={async () => {
                        setTestingWebhook(true);
                        setWebhookTestResult(null);
                        try {
                          const response = await fetch(`${API_BASE}/api/cashx/orders`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'X-API-Key': 'zozo-3a831ac1-30028892-bea62b66' },
                            body: JSON.stringify({
                              order_number: `TEST-${Date.now()}`,
                              source: 'webhook_test',
                              delivery_type: 'pickup',
                              customer: { name: 'Webhook Test' },
                              items: [{ name: 'Test Artikel', quantity: 1, price: 0.01 }],
                              total: 0.01
                            })
                          });
                          const data = await response.json();
                          if (response.ok && data.success) {
                            setWebhookTestResult({ success: true, message: 'Verbindung erfolgreich! Test-Bestellung empfangen.' });
                            toast.success('🎉 Webhook-Test erfolgreich!');
                            fetchOrders();
                          } else {
                            setWebhookTestResult({ success: false, message: data.error || 'Fehler beim Test' });
                          }
                        } catch (error) {
                          setWebhookTestResult({ success: false, message: 'Verbindungsfehler' });
                        } finally {
                          setTestingWebhook(false);
                        }
                      }}
                      disabled={testingWebhook}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {testingWebhook ? <RefreshCw className="animate-spin mr-2" size={14} /> : <Bell size={14} className="mr-2" />}
                      Webhook testen
                    </Button>
                    {webhookTestResult && (
                      <div className={`flex items-center gap-2 text-sm ${webhookTestResult.success ? 'text-emerald-400' : 'text-red-400'}`}>
                        {webhookTestResult.success ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                        {webhookTestResult.message}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Portal Integrations */}
            <div>
              <h3 className="font-semibold text-white mb-3">Lieferportale verbinden</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'lieferando', name: 'Lieferando', company: 'Takeaway.com', color: '#FF8000', icon: Bike, fields: ['apiKey', 'storeId'] },
                  { id: 'wolt', name: 'Wolt', company: 'DoorDash', color: '#009DE0', icon: Bike, fields: ['apiKey', 'merchantId'] },
                  { id: 'ubereats', name: 'Uber Eats', company: 'Uber Technologies', color: '#06C167', icon: Car, fields: ['clientId', 'clientSecret', 'storeId'] },
                  { id: 'justeat', name: 'Just Eat', company: 'Just Eat Takeaway', color: '#FF5A00', icon: Bike, fields: ['apiKey', 'restaurantId'] },
                  { id: 'deliveroo', name: 'Deliveroo', company: 'Deliveroo PLC', color: '#00CCBC', icon: Bike, fields: ['apiKey', 'locationId'] },
                  { id: 'foodora', name: 'Foodora', company: 'Delivery Hero', color: '#D70F64', icon: Bike, fields: ['apiKey', 'vendorId'] },
                  { id: 'mjam', name: 'Mjam', company: 'Lieferando AT', color: '#E63C2F', icon: Bike, fields: ['apiKey', 'restaurantId'] },
                  { id: 'website', name: 'Eigene Website', company: 'ZOZO Webshop', color: 'gradient', icon: Globe, fields: ['webhookSecret'] },
                ].map(portal => {
                  const PortalIcon = portal.icon;
                  const isEnabled = portalConfigs[portal.id]?.enabled;
                  return (
                    <div key={portal.id} className={`p-4 bg-[#1a1a24] rounded-lg border border-gray-800 hover:border-opacity-50 transition-all`} style={{ borderColor: isEnabled ? `${portal.color}50` : undefined }}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${portal.color === 'gradient' ? 'bg-gradient-to-br from-purple-500 to-pink-500' : ''}`} style={portal.color !== 'gradient' ? { backgroundColor: portal.color } : {}}>
                            <PortalIcon size={20} className="text-white" />
                          </div>
                          <div>
                            <div className="font-semibold">{portal.name}</div>
                            <div className="text-xs text-gray-500">{portal.company}</div>
                          </div>
                        </div>
                        <Badge className={isEnabled ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-gray-500/20 text-gray-400 border-gray-500/30'}>
                          {isEnabled ? 'Aktiv' : 'Nicht verbunden'}
                        </Badge>
                      </div>
                      <Button 
                        size="sm" 
                        className={`w-full ${isEnabled ? 'bg-transparent border hover:bg-white/5' : ''}`}
                        style={isEnabled ? { borderColor: `${portal.color}50`, color: portal.color === 'gradient' ? '#a855f7' : portal.color } : { backgroundColor: portal.color === 'gradient' ? '#a855f7' : portal.color }}
                        onClick={() => { setSelectedPortal(portal); setShowPortalConfigModal(true); }}
                      >
                        {isEnabled ? <><Settings size={14} className="mr-1" />Konfigurieren</> : <><Plus size={14} className="mr-1" />Verbinden</>}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* API Documentation */}
            <div className="p-4 bg-[#1a1a24] rounded-lg border border-gray-800">
              <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                <Monitor size={16} className="text-blue-400" />
                API Dokumentation
              </h3>
              <div className="p-3 bg-[#0a0a0f] rounded font-mono text-xs">
                <div className="text-gray-400"># Bestellung senden</div>
                <div className="text-emerald-400">POST /api/cashx/orders</div>
                <div className="text-amber-400 mt-1">X-API-Key: [Ihr API-Key]</div>
                <div className="text-blue-300 mt-2">{`{ "order_number": "UE-12345", "source": "ubereats", "delivery_type": "delivery", "customer": { "name": "...", "phone": "...", "address": "..." }, "items": [{ "name": "...", "quantity": 1, "price": 9.90 }], "total": 9.90 }`}</div>
              </div>
              <p className="text-gray-500 text-xs mt-2">Quellen: lieferando, wolt, ubereats, justeat, deliveroo, foodora, mjam, website, phone, pos</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIntegrationsModal(false)} className="border-gray-700">Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Portal Configuration Modal */}
      <Dialog open={showPortalConfigModal} onOpenChange={setShowPortalConfigModal}>
        <DialogContent className="bg-[#12121a] border-gray-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedPortal && (
                <>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedPortal.color === 'gradient' ? 'bg-gradient-to-br from-purple-500 to-pink-500' : ''}`} style={selectedPortal.color !== 'gradient' ? { backgroundColor: selectedPortal.color } : {}}>
                    {selectedPortal.icon && <selectedPortal.icon size={20} className="text-white" />}
                  </div>
                  <div>
                    <div>{selectedPortal.name} Konfiguration</div>
                    <div className="text-xs text-gray-500 font-normal">{selectedPortal.company}</div>
                  </div>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPortal && (
            <div className="space-y-4 py-4">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-3 bg-[#1a1a24] rounded-lg">
                <div>
                  <div className="font-medium">Integration aktivieren</div>
                  <div className="text-xs text-gray-500">Bestellungen von {selectedPortal.name} empfangen</div>
                </div>
                <Button
                  variant={portalConfigs[selectedPortal.id]?.enabled ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPortalConfigs(prev => ({ ...prev, [selectedPortal.id]: { ...prev[selectedPortal.id], enabled: !prev[selectedPortal.id]?.enabled } }))}
                  className={portalConfigs[selectedPortal.id]?.enabled ? 'bg-emerald-600' : 'border-gray-700'}
                >
                  {portalConfigs[selectedPortal.id]?.enabled ? <><Check size={14} className="mr-1" />Aktiv</> : 'Deaktiviert'}
                </Button>
              </div>

              {/* API Credentials */}
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-gray-400">API-Zugangsdaten von {selectedPortal.name}</h4>
                
                {selectedPortal.id === 'ubereats' && (
                  <>
                    <div>
                      <Label className="text-gray-400 text-xs">Client ID</Label>
                      <Input placeholder="Uber Eats Client ID eingeben..." className="mt-1 bg-[#0a0a0f] border-gray-700" value={portalConfigs.ubereats?.clientId || ''} onChange={(e) => setPortalConfigs(prev => ({ ...prev, ubereats: { ...prev.ubereats, clientId: e.target.value } }))} />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-xs">Client Secret</Label>
                      <Input type="password" placeholder="Uber Eats Client Secret eingeben..." className="mt-1 bg-[#0a0a0f] border-gray-700" value={portalConfigs.ubereats?.clientSecret || ''} onChange={(e) => setPortalConfigs(prev => ({ ...prev, ubereats: { ...prev.ubereats, clientSecret: e.target.value } }))} />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-xs">Store ID</Label>
                      <Input placeholder="Ihre Uber Eats Store ID..." className="mt-1 bg-[#0a0a0f] border-gray-700" value={portalConfigs.ubereats?.storeId || ''} onChange={(e) => setPortalConfigs(prev => ({ ...prev, ubereats: { ...prev.ubereats, storeId: e.target.value } }))} />
                    </div>
                  </>
                )}
                
                {(selectedPortal.id === 'lieferando' || selectedPortal.id === 'wolt' || selectedPortal.id === 'justeat' || selectedPortal.id === 'deliveroo' || selectedPortal.id === 'foodora' || selectedPortal.id === 'mjam') && (
                  <>
                    <div>
                      <Label className="text-gray-400 text-xs">API Key</Label>
                      <Input type="password" placeholder={`${selectedPortal.name} API Key eingeben...`} className="mt-1 bg-[#0a0a0f] border-gray-700" value={portalConfigs[selectedPortal.id]?.apiKey || ''} onChange={(e) => setPortalConfigs(prev => ({ ...prev, [selectedPortal.id]: { ...prev[selectedPortal.id], apiKey: e.target.value } }))} />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-xs">{selectedPortal.id === 'wolt' ? 'Merchant ID' : selectedPortal.id === 'deliveroo' ? 'Location ID' : selectedPortal.id === 'foodora' ? 'Vendor ID' : 'Restaurant/Store ID'}</Label>
                      <Input placeholder={`Ihre ${selectedPortal.name} ID...`} className="mt-1 bg-[#0a0a0f] border-gray-700" />
                    </div>
                  </>
                )}
                
                {selectedPortal.id === 'website' && (
                  <div>
                    <Label className="text-gray-400 text-xs">Webhook Secret (optional)</Label>
                    <Input type="password" placeholder="Geheimer Schlüssel für Signaturvalidierung..." className="mt-1 bg-[#0a0a0f] border-gray-700" value={portalConfigs.website?.webhookSecret || ''} onChange={(e) => setPortalConfigs(prev => ({ ...prev, website: { ...prev.website, webhookSecret: e.target.value } }))} />
                    <p className="text-xs text-gray-500 mt-1">Wird zur Validierung eingehender Webhooks verwendet</p>
                  </div>
                )}
              </div>

              {/* Help Text */}
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-xs text-blue-300">
                <div className="font-medium mb-1">So verbinden Sie {selectedPortal.name}:</div>
                <ol className="list-decimal list-inside space-y-1 text-blue-200">
                  <li>Loggen Sie sich in Ihr {selectedPortal.name} Partner-Portal ein</li>
                  <li>Navigieren Sie zu Einstellungen → API/Integrationen</li>
                  <li>Erstellen Sie einen neuen API-Zugang</li>
                  <li>Kopieren Sie die Zugangsdaten hierher</li>
                  <li>Speichern Sie die Konfiguration</li>
                </ol>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPortalConfigModal(false)} className="border-gray-700">Abbrechen</Button>
            <Button 
              onClick={() => { 
                toast.success(`${selectedPortal?.name} Konfiguration gespeichert!`); 
                setShowPortalConfigModal(false); 
              }} 
              style={{ backgroundColor: selectedPortal?.color === 'gradient' ? '#a855f7' : selectedPortal?.color }}
            >
              <Check size={14} className="mr-1" />Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NEW: Delivery/Pickup Order Modal */}
      <Dialog open={showDeliveryPickupModal} onOpenChange={setShowDeliveryPickupModal}>
        <DialogContent className="bg-[#12121a] border-gray-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              {!deliveryPickupType ? (
                <>
                  <Package className="text-amber-400" />
                  Neue Bestellung
                </>
              ) : deliveryPickupType === 'pickup' ? (
                <>
                  <Store className="text-emerald-400" />
                  Abholung
                </>
              ) : (
                <>
                  <Truck className="text-blue-400" />
                  Lieferung
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Step 1: Choose Type */}
          {!deliveryPickupType && (
            <div className="grid grid-cols-2 gap-4 py-6">
              <button
                onClick={() => {
                  setDeliveryPickupType('pickup');
                  setOrderTarget('pickup');
                }}
                className="p-8 rounded-xl border-2 border-gray-700 hover:border-emerald-500 hover:bg-emerald-500/10 transition-all group"
              >
                <Store className="w-16 h-16 mx-auto mb-4 text-emerald-400 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-bold text-white">Abholung</h3>
                <p className="text-gray-400 text-sm mt-2">Kunde holt selbst ab</p>
              </button>
              
              <button
                onClick={() => setDeliveryPickupType('delivery')}
                className="p-8 rounded-xl border-2 border-gray-700 hover:border-blue-500 hover:bg-blue-500/10 transition-all group"
              >
                <Truck className="w-16 h-16 mx-auto mb-4 text-blue-400 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-bold text-white">Lieferung</h3>
                <p className="text-gray-400 text-sm mt-2">Wir liefern zum Kunden</p>
              </button>
            </div>
          )}

          {/* Step 2a: Pickup - Just name and optional time */}
          {deliveryPickupType === 'pickup' && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-gray-400">Kundenname</Label>
                <Input
                  value={deliveryCustomer.name}
                  onChange={(e) => setDeliveryCustomer({...deliveryCustomer, name: e.target.value})}
                  placeholder="Name des Kunden"
                  className="mt-1 bg-[#1a1a24] border-gray-700"
                />
              </div>
              
              <div>
                <Label className="text-gray-400">Telefon (optional)</Label>
                <Input
                  value={deliveryCustomer.phone}
                  onChange={(e) => setDeliveryCustomer({...deliveryCustomer, phone: e.target.value})}
                  placeholder="Telefonnummer"
                  className="mt-1 bg-[#1a1a24] border-gray-700"
                />
              </div>

              {/* Scheduled Order Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock className="text-amber-400" size={18} />
                  <span>Vorbestellung (Zeitbestellung)</span>
                </div>
                <button
                  onClick={() => setIsScheduledOrder(!isScheduledOrder)}
                  className={`w-12 h-6 rounded-full transition-all ${isScheduledOrder ? 'bg-amber-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-all ${isScheduledOrder ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {isScheduledOrder && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-400">Datum</Label>
                    <Input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="mt-1 bg-[#1a1a24] border-gray-700"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">Uhrzeit</Label>
                    <Input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="mt-1 bg-[#1a1a24] border-gray-700"
                    />
                  </div>
                </div>
              )}

              <Button
                onClick={() => {
                  setCustomerName(deliveryCustomer.name);
                  setCustomerPhone(deliveryCustomer.phone);
                  setShowDeliveryPickupModal(false);
                }}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white py-3"
              >
                <ShoppingCart className="mr-2" size={18} />
                Weiter zur Bestellung
              </Button>
            </div>
          )}

          {/* Step 2b: Delivery - Full customer details */}
          {deliveryPickupType === 'delivery' && (
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-gray-400">Kundenname *</Label>
                  <Input
                    value={deliveryCustomer.name}
                    onChange={(e) => setDeliveryCustomer({...deliveryCustomer, name: e.target.value})}
                    placeholder="Vor- und Nachname"
                    className="mt-1 bg-[#1a1a24] border-gray-700"
                    required
                  />
                </div>
                
                <div className="col-span-2">
                  <Label className="text-gray-400">Telefon *</Label>
                  <Input
                    value={deliveryCustomer.phone}
                    onChange={(e) => setDeliveryCustomer({...deliveryCustomer, phone: e.target.value})}
                    placeholder="Telefonnummer"
                    className="mt-1 bg-[#1a1a24] border-gray-700"
                    required
                  />
                </div>

                <div className="col-span-2 border-t border-gray-700 pt-3 mt-2">
                  <Label className="text-gray-400 flex items-center gap-2">
                    <MapPin size={14} /> Lieferadresse
                  </Label>
                </div>

                <div>
                  <Label className="text-gray-400">Straße *</Label>
                  <Input
                    value={deliveryCustomer.street}
                    onChange={(e) => setDeliveryCustomer({...deliveryCustomer, street: e.target.value})}
                    placeholder="Straßenname"
                    className="mt-1 bg-[#1a1a24] border-gray-700"
                    required
                  />
                </div>
                
                <div>
                  <Label className="text-gray-400">Hausnummer *</Label>
                  <Input
                    value={deliveryCustomer.houseNumber}
                    onChange={(e) => setDeliveryCustomer({...deliveryCustomer, houseNumber: e.target.value})}
                    placeholder="Nr."
                    className="mt-1 bg-[#1a1a24] border-gray-700"
                    required
                  />
                </div>

                <div>
                  <Label className="text-gray-400">PLZ *</Label>
                  <Input
                    value={deliveryCustomer.postalCode}
                    onChange={(e) => setDeliveryCustomer({...deliveryCustomer, postalCode: e.target.value})}
                    placeholder="PLZ"
                    className="mt-1 bg-[#1a1a24] border-gray-700"
                    required
                  />
                </div>
                
                <div>
                  <Label className="text-gray-400">Stadt *</Label>
                  <Input
                    value={deliveryCustomer.city}
                    onChange={(e) => setDeliveryCustomer({...deliveryCustomer, city: e.target.value})}
                    placeholder="Stadt"
                    className="mt-1 bg-[#1a1a24] border-gray-700"
                    required
                  />
                </div>

                <div className="col-span-2">
                  <Label className="text-gray-400">Etage / Klingel (optional)</Label>
                  <Input
                    value={deliveryCustomer.floor}
                    onChange={(e) => setDeliveryCustomer({...deliveryCustomer, floor: e.target.value})}
                    placeholder="z.B. 3. OG links, Klingel Müller"
                    className="mt-1 bg-[#1a1a24] border-gray-700"
                  />
                </div>

                <div className="col-span-2">
                  <Label className="text-gray-400">Hinweise für Fahrer</Label>
                  <Input
                    value={deliveryCustomer.notes}
                    onChange={(e) => setDeliveryCustomer({...deliveryCustomer, notes: e.target.value})}
                    placeholder="z.B. Hintereingang, Hund im Garten..."
                    className="mt-1 bg-[#1a1a24] border-gray-700"
                  />
                </div>
              </div>

              {/* Scheduled Order Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock className="text-amber-400" size={18} />
                  <span>Vorbestellung (Zeitbestellung)</span>
                </div>
                <button
                  onClick={() => setIsScheduledOrder(!isScheduledOrder)}
                  className={`w-12 h-6 rounded-full transition-all ${isScheduledOrder ? 'bg-amber-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-all ${isScheduledOrder ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {isScheduledOrder && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-400">Datum</Label>
                    <Input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="mt-1 bg-[#1a1a24] border-gray-700"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">Gewünschte Uhrzeit</Label>
                    <Input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="mt-1 bg-[#1a1a24] border-gray-700"
                    />
                  </div>
                </div>
              )}

              <Button
                onClick={() => {
                  if (!deliveryCustomer.name || !deliveryCustomer.phone || !deliveryCustomer.street || 
                      !deliveryCustomer.houseNumber || !deliveryCustomer.postalCode || !deliveryCustomer.city) {
                    toast.error('Bitte füllen Sie alle Pflichtfelder aus');
                    return;
                  }
                  setCustomerName(deliveryCustomer.name);
                  setCustomerPhone(deliveryCustomer.phone);
                  setOrderTarget('delivery');
                  setShowDeliveryPickupModal(false);
                }}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3"
              >
                <ShoppingCart className="mr-2" size={18} />
                Weiter zur Bestellung
              </Button>
            </div>
          )}

          {/* Back Button */}
          {deliveryPickupType && (
            <Button
              variant="ghost"
              onClick={() => setDeliveryPickupType(null)}
              className="mt-2"
            >
              ← Zurück zur Auswahl
            </Button>
          )}
        </DialogContent>
      </Dialog>

      {/* Printer Settings Modal */}
      <Dialog open={showPrinterSettings} onOpenChange={setShowPrinterSettings}>
        <DialogContent className="bg-[#12121a] border-gray-800 text-white max-w-4xl max-h-[85vh] overflow-y-auto">
          <PrinterSettings 
            sessionToken={sessionToken} 
            onClose={() => setShowPrinterSettings(false)} 
          />
        </DialogContent>
      </Dialog>
      
      {/* FEATURE G: Split Bill Modal */}
      <SplitBillModal
        isOpen={showSplitBillModal}
        onClose={() => setShowSplitBillModal(false)}
        cart={cart}
        totalAmount={cartTotal}
      />
      
      {/* FEATURE H: Tip Modal */}
      <TipModal
        isOpen={showTipModal}
        onClose={() => setShowTipModal(false)}
        orderId={lastCompletedOrderId}
        orderTotal={cartTotal}
      />
    </div>
  );
}
